import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";

function SettledBill() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [ledgerParties, setLedgerParties] = useState([]);
  const [banks, setBanks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankBalances, setBankBalances] = useState({}); // Store bank balances
  const [bankPaymentFields, setBankPaymentFields] = useState({}); // Store bank payment fields: { bankId: { amount: '', isSettled: false } }
  const [paymentMethodFields, setPaymentMethodFields] = useState({}); // Store payment method fields: { pmId: { amount: '', isSettled: false } }
  const [selectedSupplierId, setSelectedSupplierId] = useState(''); // Shared supplier for all banks/payment methods
  const [selectedBillRowId, setSelectedBillRowId] = useState(null); // Track which specific bill row is selected
  const [settledSuppliers, setSettledSuppliers] = useState([]); // Suppliers with settled bills
  const bankInputRefs = useRef({}); // Refs for bank input fields for navigation
  const paymentMethodInputRefs = useRef({}); // Refs for payment method input fields
  const expenseFieldInputRefs = useRef({}); // Refs for expense field inputs
  const settledCheckboxRef = useRef(null); // Ref for shared settled checkbox
  const settleButtonRef = useRef(null); // Ref for settle button
  const [isSettledChecked, setIsSettledChecked] = useState(false); // State for shared settled checkbox
  const [expenseFieldInputs, setExpenseFieldInputs] = useState({}); // Store input values for expense fields

  // Supplier Roz Namacha Data
  const [supplierRows, setSupplierRows] = useState([
    {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      supplierName: '',
      sale: '',
      cash: '',
      balance: ''
    }
  ]);

  // Customer Roz Namacha Data
  const [customerRows, setCustomerRows] = useState([
    {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      receiptNumber: '',
      supplierName: '',
      customerName: '',
      sale: '',
      cash: '',
      balance: ''
    }
  ]);

  const [editingRowId, setEditingRowId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bills, setBills] = useState([]);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [selectedBillSettlement, setSelectedBillSettlement] = useState(null);

  // Refs for debouncing auto-save
  const supplierAutoSaveTimer = useRef(null);
  const customerAutoSaveTimer = useRef(null);

  // Dynamic columns from admin settings
  const [supplierColumns, setSupplierColumns] = useState([
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "billNumber", labelEn: "Bill Number", labelUr: "بل نمبر", type: "text", isDefault: true },
    { key: "supplierName", labelEn: "Supplier", labelUr: "سپلائر", type: "text", isDefault: true },
    { key: "sale", labelEn: "Sale", labelUr: "سیل", type: "number", isDefault: true },
    { key: "cash", labelEn: "Cash", labelUr: "نقدی", type: "number", isDefault: true },
    { key: "amount", labelEn: "Cash Amount", labelUr: "نقد رقم", type: "number", isDefault: true },
    { key: "balance", labelEn: "Balance", labelUr: "بقایا", type: "number", isDefault: true },
  ]);

  const [customerColumns, setCustomerColumns] = useState([
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "receiptNumber", labelEn: "Receipt Number", labelUr: "رسید نمبر", type: "text", isDefault: true },
    { key: "supplierName", labelEn: "Supplier", labelUr: "سپلائر", type: "text", isDefault: true },
    { key: "customerName", labelEn: "Customer", labelUr: "گاہک", type: "text", isDefault: true },
    { key: "sale", labelEn: "Sale", labelUr: "سیل", type: "number", isDefault: true },
    { key: "cash", labelEn: "Cash", labelUr: "نقدی", type: "number", isDefault: true },
    { key: "balance", labelEn: "Balance", labelUr: "بقایا", type: "number", isDefault: true },
  ]);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    if (company) {
      // Fetch banks and payment methods first, then load saved columns (which will be synced)
      fetchBanks();
      fetchPaymentMethods();
      loadSavedColumns();
      loadSavedRows(); // Load saved rows first
      fetchBills(); // Then fetch bills to update if needed
    }
  }, [company]);

  // Helper function to sort supplier rows by bill number (sequence)
  const sortSupplierRowsBySequence = (rows) => {
    if (!Array.isArray(rows)) return rows;

    return [...rows].sort((a, b) => {
      // Sort by bill number/serial number first (sequence order)
      const billNumA = a.billNumber || a.billNo || a.serialNumber || '';
      const billNumB = b.billNumber || b.billNo || b.serialNumber || '';

      // Try numeric comparison first
      const numA = parseInt(billNumA);
      const numB = parseInt(billNumB);

      if (!isNaN(numA) && !isNaN(numB)) {
        // Both are numbers, compare numerically
        if (numA !== numB) return numA - numB;
      } else {
        // String comparison with numeric sensitivity
        const strCompare = billNumA.localeCompare(billNumB, undefined, { numeric: true, sensitivity: 'base' });
        if (strCompare !== 0) return strCompare;
      }

      // If bill numbers are same, sort by date (oldest first)
      if (a.date && b.date) {
        return a.date.localeCompare(b.date);
      }

      // If dates are same or missing, sort by billId
      const idA = (a.billId || a.id)?.toString() || '';
      const idB = (b.billId || b.id)?.toString() || '';
      return idA.localeCompare(idB);
    });
  };

  // Helper function to set settled suppliers with sorting and saving
  const setSettledSuppliersSorted = (newSettled) => {
    const sortedSettled = sortSupplierRowsBySequence(newSettled);
    setSettledSuppliers(sortedSettled);

    // Save to localStorage
    if (company?._id) {
      try {
        const settledKey = `rozSettledSuppliers_${company._id}`;
        localStorage.setItem(settledKey, JSON.stringify(sortedSettled));
      } catch (error) {
        console.error("Error saving settled suppliers:", error);
      }
    }

    return sortedSettled;
  };

  // Debug: Log banks state changes
  useEffect(() => {
    console.log('🔄 Banks state updated:', banks.length, 'banks:', banks);
  }, [banks]);

  // Fetch bank balances when banks or payment methods are loaded
  useEffect(() => {
    if ((banks.length > 0 || paymentMethods.length > 0) && company?._id) {
      fetchBankBalances();
    }
  }, [banks.length, paymentMethods.length, company?._id]);

  // Refresh bank balances periodically or when component is focused
  useEffect(() => {
    if ((banks.length > 0 || paymentMethods.length > 0) && company?._id) {
      // Refresh balances when component mounts or becomes visible
      const refreshBalances = () => {
        if (document.visibilityState === 'visible') {
          fetchBankBalances();
        }
      };

      document.addEventListener('visibilitychange', refreshBalances);

      // Also refresh after a short delay to ensure all data is loaded
      const timer = setTimeout(() => {
        fetchBankBalances();
      }, 1000);

      return () => {
        document.removeEventListener('visibilitychange', refreshBalances);
        clearTimeout(timer);
      };
    }
  }, [banks.length, paymentMethods.length, company?._id]);

  // Fetch sales after company is loaded
  useEffect(() => {
    if (company?._id) {
      // Small delay to ensure saved rows are loaded first
      const timer = setTimeout(() => {
        fetchSales();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [company?._id]);

  // Initialize expense field inputs with bill values when bill is selected
  useEffect(() => {
    if (selectedBillRowId && bills.length > 0 && supplierRows.length > 0) {
      const supplierRow = supplierRows.find(row => row.id === selectedBillRowId || row.billId === selectedBillRowId);
      if (supplierRow) {
        const billId = supplierRow.billId || supplierRow.id;
        const fullBill = bills.find(b => b._id?.toString() === billId?.toString());

        if (fullBill) {
          const billExpenses = fullBill.expenses || {};

          // Define field mapping
          const fieldMapping = {
            'نقدی': ['Cash', 'نقدی'],
            'کرایه': ['Rent', 'کرایه', 'Kiraya'],
            'کمیشن': ['Commission', 'کمیشن'],
            'مزدوری': ['Labor', 'Wages', 'مزدوری', 'Mazdoori'],
            'مشیانه': ['Miscellaneous', 'Mashiyana', 'مشیانہ', 'مشیانه', 'Mashiana'],
            'مارکیٹ_فیس': ['Market Fees', 'Market Fee', 'مارکیٹ_فیس', 'مارکیٹ فیس', 'MarketFees']
          };

          const initialValues = {};

          // Map each field to its value from bill expenses
          Object.entries(fieldMapping).forEach(([fieldKey, possibleKeys]) => {
            let value = 0;

            // Try to find value from billExpenses
            for (const key of possibleKeys) {
              if (billExpenses[key] !== undefined && billExpenses[key] !== null) {
                value = parseFloat(billExpenses[key] || 0);
                if (value > 0) break;
              }
            }

            // Store the value with the field key
            if (value > 0) {
              const normalizedKey = fieldKey.toLowerCase().replace(/\s+/g, '_');
              initialValues[normalizedKey] = value.toString();
            }
          });

          // Only update if there are values to set
          if (Object.keys(initialValues).length > 0) {
            setExpenseFieldInputs(prev => {
              // Merge with existing values, but don't overwrite if user has already entered something
              const merged = { ...prev };
              Object.entries(initialValues).forEach(([key, value]) => {
                // Only set if not already set by user
                if (!merged[key] || merged[key] === '') {
                  merged[key] = value;
                }
              });
              return merged;
            });
          }
        }
      }
    } else if (!selectedBillRowId && !selectedSupplierId) {
      // Clear expense field inputs when no bill is selected
      setExpenseFieldInputs({});
    }
  }, [selectedBillRowId, bills, supplierRows, selectedSupplierId]);

  const loadSavedRows = () => {
    if (!company?._id) return;

    try {
      const supplierKey = `rozSupplierRows_${company._id}`;
      const customerKey = `rozCustomerRows_${company._id}`;
      const settledKey = `rozSettledSuppliers_${company._id}`;

      const savedSupplierRows = localStorage.getItem(supplierKey);
      const savedCustomerRows = localStorage.getItem(customerKey);
      const savedSettledSuppliers = localStorage.getItem(settledKey);

      // Load settled suppliers first
      let loadedSettledSuppliers = [];
      if (savedSettledSuppliers) {
        const settled = JSON.parse(savedSettledSuppliers);
        if (Array.isArray(settled) && settled.length > 0) {
          loadedSettledSuppliers = settled;
          // Sort settled suppliers by bill number sequence
          const sortedSettled = sortSupplierRowsBySequence(settled);
          setSettledSuppliers(sortedSettled);
          console.log("Loaded settled suppliers from localStorage:", sortedSettled.length);
        }
      }

      if (savedSupplierRows) {
        const rows = JSON.parse(savedSupplierRows);
        if (Array.isArray(rows) && rows.length > 0) {
          // Filter out bills that are already in settled list (by billId, not supplierId)
          const settledBillIds = loadedSettledSuppliers.map(s => {
            const sBillId = s.billId || s.id;
            return sBillId ? sBillId.toString() : null;
          }).filter(id => id !== null);

          // Only keep bills that are not settled (check by billId)
          const activeRows = rows.filter(row => {
            const rowBillId = row.billId || row.id;
            const rowBillIdStr = rowBillId ? rowBillId.toString() : null;
            return !settledBillIds.includes(rowBillIdStr);
          });

          // Initialize saved values for bank fields
          const rowsWithSavedValues = activeRows.map(row => {
            const updatedRow = { ...row };
            supplierColumns.forEach(col => {
              if (col.key && col.key.startsWith('bank_')) {
                const currentValue = parseFloat(row[col.key] || 0);
                // If saved value doesn't exist, initialize it with current value
                if (!updatedRow[`_saved_${col.key}`] && currentValue > 0.01) {
                  updatedRow[`_saved_${col.key}`] = currentValue;
                }
              }
            });
            return updatedRow;
          });
          // Sort by bill number sequence before setting
          const sortedRows = sortSupplierRowsBySequence(rowsWithSavedValues);
          setSupplierRows(sortedRows);
          console.log("Loaded saved supplier rows from localStorage:", rowsWithSavedValues.length);
        }
      }

      if (savedCustomerRows) {
        const rows = JSON.parse(savedCustomerRows);
        if (Array.isArray(rows) && rows.length > 0) {
          // Initialize saved values for bank fields
          const rowsWithSavedValues = rows.map(row => {
            const updatedRow = { ...row };
            customerColumns.forEach(col => {
              if (col.key && col.key.startsWith('bank_')) {
                const currentValue = parseFloat(row[col.key] || 0);
                // If saved value doesn't exist, initialize it with current value
                if (!updatedRow[`_saved_${col.key}`] && currentValue > 0.01) {
                  updatedRow[`_saved_${col.key}`] = currentValue;
                }
              }
            });
            return updatedRow;
          });
          setCustomerRows(rowsWithSavedValues);
          console.log("Loaded saved customer rows from localStorage:", rowsWithSavedValues.length);
        }
      }
    } catch (error) {
      console.error("Error loading saved rows:", error);
    }
  };

  const saveRowsToLocalStorage = () => {
    if (!company?._id) return;

    try {
      const supplierKey = `rozSupplierRows_${company._id}`;
      const customerKey = `rozCustomerRows_${company._id}`;

      localStorage.setItem(supplierKey, JSON.stringify(supplierRows));
      localStorage.setItem(customerKey, JSON.stringify(customerRows));
      console.log("Saved rows to localStorage");
    } catch (error) {
      console.error("Error saving rows to localStorage:", error);
    }
  };

  const loadSavedColumns = () => {
    if (!company?._id) return;

    try {
      const storageKey = `rozTemplate_${company._id}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const tpl = JSON.parse(raw);
        console.log("Loading saved columns for Roz Namacha:", storageKey, tpl);

        // Check if saved columns have easypaisa - if yes, skip loading them (will be synced with banks)
        const supplierHasEasypaisa = (tpl.supplierColumns || []).some(col => col.key === 'easypaisa');
        const customerHasEasypaisa = (tpl.customerColumns || []).some(col => col.key === 'easypaisa');

        if (supplierHasEasypaisa) {
          console.log("⚠️ Found easypaisa in saved supplier columns, skipping load - banks will sync");
        } else if (tpl.supplierColumns && Array.isArray(tpl.supplierColumns) && tpl.supplierColumns.length > 0) {
          // Ensure billNumber and amount columns are included (merge with saved columns)
          const savedColumns = tpl.supplierColumns;
          const hasBillNumber = savedColumns.some(col => col.key === 'billNumber');
          const hasAmount = savedColumns.some(col => col.key === 'amount');

          if (!hasBillNumber) {
            // Insert billNumber after date column
            const dateIndex = savedColumns.findIndex(col => col.key === 'date');
            if (dateIndex >= 0) {
              savedColumns.splice(dateIndex + 1, 0, {
                key: "billNumber",
                labelEn: "Bill Number",
                labelUr: "بل نمبر",
                type: "text",
                isDefault: true
              });
              console.log("✅ Added billNumber column to saved columns");
            }
          }

          if (!hasAmount) {
            // Insert amount after cash column
            const cashIndex = savedColumns.findIndex(col => col.key === 'cash');
            if (cashIndex >= 0) {
              savedColumns.splice(cashIndex + 1, 0, {
                key: "amount",
                labelEn: "Cash Amount",
                labelUr: "نقد رقم",
                type: "number",
                isDefault: true
              });
              console.log("✅ Added amount column to saved columns");
            }
          }

          setSupplierColumns(savedColumns);
          console.log("✅ Loaded supplier columns:", savedColumns.length);
        }

        if (customerHasEasypaisa) {
          console.log("⚠️ Found easypaisa in saved customer columns, skipping load - banks will sync");
        } else if (tpl.customerColumns && Array.isArray(tpl.customerColumns) && tpl.customerColumns.length > 0) {
          setCustomerColumns(tpl.customerColumns);
          console.log("✅ Loaded customer columns:", tpl.customerColumns.length);
        }
      } else {
        console.log("No saved columns found in localStorage");
      }
    } catch (error) {
      console.error("Error loading saved columns:", error);
    }
  };

  // Helper function to sync payment methods with columns
  const syncPaymentMethodsWithColumns = (existingColumns, paymentMethodsData, companyId, type) => {
    if (!paymentMethodsData || paymentMethodsData.length === 0) {
      console.log(`No payment methods to sync for ${type} columns`);
      return existingColumns;
    }

    console.log(`Syncing ${paymentMethodsData.length} payment methods with ${type} columns. Existing columns:`, existingColumns.length);

    // Find existing payment method columns
    const existingPaymentMethodKeys = new Set(
      existingColumns
        .filter(col => col.key && col.key.startsWith('paymentMethod_'))
        .map(col => col.key)
    );

    console.log(`Existing payment method keys:`, Array.from(existingPaymentMethodKeys));

    // Create payment method columns for all active payment methods
    const paymentMethodColumns = paymentMethodsData
      .filter(method => method.isActive !== false) // Only active methods
      .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
      .map(method => ({
        key: `paymentMethod_${method._id}`,
        labelEn: method.name || 'Payment Method',
        labelUr: method.nameUrdu || method.name || 'پے منٹ',
        type: 'number',
        isDefault: false,
        paymentMethodId: method._id,
        paymentMethodName: method.name,
        paymentMethodNameUrdu: method.nameUrdu
      }));

    // Remove existing payment method columns, then add new ones
    const columnsWithoutPaymentMethods = existingColumns.filter(col => {
      return !col.key.startsWith('paymentMethod_');
    });

    // Find the position after banks (or after cash if no banks)
    const lastBankIndex = columnsWithoutPaymentMethods.findLastIndex(col => col.key && col.key.startsWith('bank_'));
    const balanceIndex = columnsWithoutPaymentMethods.findIndex(col => col.key === 'balance');

    // Insert payment method columns after banks (or before balance if no banks)
    let finalColumns;
    if (lastBankIndex >= 0) {
      // Insert payment methods after last bank
      finalColumns = [
        ...columnsWithoutPaymentMethods.slice(0, lastBankIndex + 1),
        ...paymentMethodColumns,
        ...columnsWithoutPaymentMethods.slice(lastBankIndex + 1)
      ];
    } else if (balanceIndex >= 0) {
      // Insert payment methods before balance
      finalColumns = [
        ...columnsWithoutPaymentMethods.slice(0, balanceIndex),
        ...paymentMethodColumns,
        ...columnsWithoutPaymentMethods.slice(balanceIndex)
      ];
    } else {
      // Just append payment methods at the end
      finalColumns = [...columnsWithoutPaymentMethods, ...paymentMethodColumns];
    }

    console.log(`✅ Added ${paymentMethodColumns.length} payment method columns to roznamacha ${type} section. Total columns: ${finalColumns.length}`);

    // Save to localStorage
    try {
      const storageKey = `rozTemplate_${companyId}`;
      const existingTemplate = localStorage.getItem(storageKey);
      let template = existingTemplate ? JSON.parse(existingTemplate) : {};

      if (type === 'supplier') {
        template.supplierColumns = finalColumns;
      } else {
        template.customerColumns = finalColumns;
      }

      localStorage.setItem(storageKey, JSON.stringify(template));
      console.log(`Saved ${type} columns with payment methods to localStorage`);
    } catch (error) {
      console.error('Error saving payment method columns:', error);
    }

    return finalColumns;
  };

  // Helper function to sync banks with columns
  const syncBanksWithColumns = (existingColumns, banksData, companyId, type) => {
    if (!banksData || banksData.length === 0) {
      console.log(`No banks to sync for ${type} columns`);
      return existingColumns;
    }

    console.log(`Syncing ${banksData.length} banks with ${type} columns. Existing columns:`, existingColumns.length);

    // Find existing bank columns
    const existingBankKeys = new Set(
      existingColumns
        .filter(col => col.key && col.key.startsWith('bank_'))
        .map(col => col.key)
    );

    console.log(`Existing bank keys:`, Array.from(existingBankKeys));

    // Create bank columns for all banks (replace easypaisa position)
    const bankColumns = banksData.map(bank => ({
      key: `bank_${bank._id}`,
      labelEn: bank.bankName || 'Bank',
      labelUr: bank.bankNameUrdu || bank.bankName || 'بینک',
      type: 'number',
      isDefault: false,
      bankId: bank._id,
      bankName: bank.bankName,
      bankNameUrdu: bank.bankNameUrdu
    }));

    // Remove easypaisa column and all existing bank columns, then add new bank columns
    const columnsWithoutEasypaisaAndBanks = existingColumns.filter(col => {
      // Keep all default columns except easypaisa, and remove all bank columns
      return col.key !== 'easypaisa' && !col.key.startsWith('bank_');
    });

    // Find the position where easypaisa was (after cash, before balance)
    const cashIndex = columnsWithoutEasypaisaAndBanks.findIndex(col => col.key === 'cash');
    const balanceIndex = columnsWithoutEasypaisaAndBanks.findIndex(col => col.key === 'balance');

    // Insert bank columns after cash (where easypaisa was)
    let finalColumns;
    if (cashIndex >= 0) {
      // Insert banks after cash
      finalColumns = [
        ...columnsWithoutEasypaisaAndBanks.slice(0, cashIndex + 1),
        ...bankColumns,
        ...columnsWithoutEasypaisaAndBanks.slice(cashIndex + 1)
      ];
    } else if (balanceIndex >= 0) {
      // Insert banks before balance
      finalColumns = [
        ...columnsWithoutEasypaisaAndBanks.slice(0, balanceIndex),
        ...bankColumns,
        ...columnsWithoutEasypaisaAndBanks.slice(balanceIndex)
      ];
    } else {
      // Just append banks at the end
      finalColumns = [...columnsWithoutEasypaisaAndBanks, ...bankColumns];
    }

    console.log(`✅ Replaced easypaisa with ${bankColumns.length} bank columns in roznamacha ${type} section. Total columns: ${finalColumns.length}`);

    // Save to localStorage
    try {
      const storageKey = `rozTemplate_${companyId}`;
      const existingTemplate = localStorage.getItem(storageKey);
      let template = existingTemplate ? JSON.parse(existingTemplate) : {};

      if (type === 'supplier') {
        template.supplierColumns = finalColumns;
      } else {
        template.customerColumns = finalColumns;
      }

      localStorage.setItem(storageKey, JSON.stringify(template));
      console.log(`Saved ${type} columns to localStorage`);
    } catch (error) {
      console.error('Error saving bank columns:', error);
    }

    return finalColumns;
  };

  // Sync banks with columns when banks change - replace easypaisa with banks
  useEffect(() => {
    if (company?._id && banks.length > 0) {
      console.log('🔄 Syncing banks with columns. Banks count:', banks.length, 'Banks:', banks.map(b => b.bankName || b._id));
      // Force sync by getting current columns and syncing
      setSupplierColumns(prevColumns => {
        console.log('Current supplier columns before sync:', prevColumns.map(c => c.key));
        const synced = syncBanksWithColumns(prevColumns, banks, company._id, 'supplier');
        console.log('✅ Supplier columns after sync:', synced.length, 'Column keys:', synced.map(c => c.key));

        // Ensure billNumber and amount columns are present
        const hasBillNumber = synced.some(c => c.key === 'billNumber');
        const hasAmount = synced.some(c => c.key === 'amount');

        if (!hasBillNumber) {
          const dateIndex = synced.findIndex(col => col.key === 'date');
          if (dateIndex >= 0) {
            synced.splice(dateIndex + 1, 0, {
              key: "billNumber",
              labelEn: "Bill Number",
              labelUr: "بل نمبر",
              type: "text",
              isDefault: true
            });
            console.log("✅ Added billNumber column after bank sync");
          }
        }

        if (!hasAmount) {
          const cashIndex = synced.findIndex(col => col.key === 'cash');
          if (cashIndex >= 0) {
            synced.splice(cashIndex + 1, 0, {
              key: "amount",
              labelEn: "Cash Amount",
              labelUr: "نقد رقم",
              type: "number",
              isDefault: true
            });
            console.log("✅ Added amount column after bank sync");
          }
        }

        // Check if easypaisa is still there
        const hasEasypaisa = synced.some(c => c.key === 'easypaisa');
        if (hasEasypaisa) {
          console.warn('⚠️ Easypaisa still present after sync!');
        }
        return synced;
      });

      setCustomerColumns(prevColumns => {
        console.log('Current customer columns before sync:', prevColumns.map(c => c.key));
        const synced = syncBanksWithColumns(prevColumns, banks, company._id, 'customer');
        console.log('✅ Customer columns after sync:', synced.length, 'Column keys:', synced.map(c => c.key));
        // Check if easypaisa is still there
        const hasEasypaisa = synced.some(c => c.key === 'easypaisa');
        if (hasEasypaisa) {
          console.warn('⚠️ Easypaisa still present after sync!');
        }
        return synced;
      });
    } else if (company?._id && banks.length === 0) {
      console.log('⚠️ No banks found for company:', company._id);
    }
  }, [banks.length, company?._id]); // Only depend on banks.length to avoid infinite loops

  // Sync payment methods with columns when payment methods change
  useEffect(() => {
    if (company?._id && paymentMethods.length > 0) {
      console.log('🔄 Syncing payment methods with columns. Payment methods count:', paymentMethods.length, 'Methods:', paymentMethods.map(pm => pm.name || pm._id));
      // Use functional updates to avoid dependency issues
      setSupplierColumns(prevColumns => {
        const synced = syncPaymentMethodsWithColumns(prevColumns, paymentMethods, company._id, 'supplier');
        console.log('✅ Supplier columns after payment method sync:', synced.length, 'Column keys:', synced.map(c => c.key));

        // Ensure billNumber and amount columns are present
        const hasBillNumber = synced.some(c => c.key === 'billNumber');
        const hasAmount = synced.some(c => c.key === 'amount');

        if (!hasBillNumber) {
          const dateIndex = synced.findIndex(col => col.key === 'date');
          if (dateIndex >= 0) {
            synced.splice(dateIndex + 1, 0, {
              key: "billNumber",
              labelEn: "Bill Number",
              labelUr: "بل نمبر",
              type: "text",
              isDefault: true
            });
            console.log("✅ Added billNumber column after payment method sync");
          }
        }

        if (!hasAmount) {
          const cashIndex = synced.findIndex(col => col.key === 'cash');
          if (cashIndex >= 0) {
            synced.splice(cashIndex + 1, 0, {
              key: "amount",
              labelEn: "Cash Amount",
              labelUr: "نقد رقم",
              type: "number",
              isDefault: true
            });
            console.log("✅ Added amount column after payment method sync");
          }
        }

        return synced;
      });

      setCustomerColumns(prevColumns => {
        const synced = syncPaymentMethodsWithColumns(prevColumns, paymentMethods, company._id, 'customer');
        console.log('✅ Customer columns after payment method sync:', synced.length, 'Column keys:', synced.map(c => c.key));
        return synced;
      });
    } else if (company?._id && paymentMethods.length === 0) {
      console.log('⚠️ No payment methods found');
    }
  }, [paymentMethods.length, company?._id]); // Only depend on paymentMethods.length to avoid infinite loops

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompany(response.data.company);
      const lang = response.data.company?.websiteLanguage || response.data.company?.language || 'en';
      setCompanyLanguage(lang);

      // Fetch ledger parties
      await fetchLedgerParties();
    } catch (error) {
      console.error("Fetch company error:", error);
      toast.error("کمپنی کا ڈیٹا لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        console.error('No token found for fetching banks');
        return;
      }
      const response = await api.get("/bank/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const banksData = response.data.banks || [];
      console.log('✅ Fetched banks for roznamacha:', banksData.length, 'banks:', banksData);
      setBanks(banksData);
      if (banksData.length === 0) {
        console.warn('⚠️ No banks found for this company');
      }
    } catch (error) {
      console.error("❌ Fetch banks error:", error);
      toast.error(companyLanguage === 'ur' ? 'بینکوں کو لوڈ کرنے میں ناکام' : 'Failed to load banks');
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      const methodsData = response.data.paymentMethods || [];
      console.log('Fetched payment methods for roznamacha:', methodsData.length, methodsData);
      setPaymentMethods(methodsData);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      toast.error(companyLanguage === 'ur' ? 'پے منٹ میتھڈز کو لوڈ کرنے میں ناکام' : 'Failed to load payment methods');
    }
  };

  const fetchBankBalances = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Get all recoveries
      const allRecoveriesResponse = await api.get(`/recovery/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const recoveries = allRecoveriesResponse.data.recoveries || [];

      // Get all expenses
      const allExpensesResponse = await api.get(`/expense-ledger/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const expenses = allExpensesResponse.data.expenses || [];

      // Calculate balance for each bank and payment method
      const balances = {};

      // Calculate balances for banks - fetch each bank's transactions separately
      if (banks.length > 0) {
        await Promise.all(banks.map(async (bank) => {
          const bankName = bank.bankName || '';
          const bankNameUrdu = bank.bankNameUrdu || bank.bankName || '';
          const bankId = bank._id?.toString() || bank._id;

          let balance = bank.openingBalance || 0;

          // Add deposits (recoveries) - only if payment method matches
          recoveries.forEach(recovery => {
            const paymentMethod = recovery.paymentMethod || '';
            if (paymentMethod === bankName || paymentMethod === bankNameUrdu) {
              balance += recovery.recoveryAmount || 0;
            }
          });

          // Subtract withdrawals (expenses) - only if payment method matches
          expenses.forEach(expense => {
            const paymentMethod = expense.paymentMethod || '';
            if (paymentMethod === bankName || paymentMethod === bankNameUrdu) {
              const expenseAmount = (expense.travellingExpense || 0) + (expense.refreshment || 0) +
                (expense.cargo || 0) + (expense.salaryExpense || 0);
              balance -= expenseAmount;
            }
          });

          // Fetch transactions from THIS bank's specific ledger
          try {
            const bankTransactionsResponse = await api.get(`/bank/company/${bankId}/transactions`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const bankTransactions = bankTransactionsResponse.data.transactions || [];

            // Process transactions from this bank's ledger only
            bankTransactions.forEach(tx => {
              // Verify transaction belongs to this bank (double-check)
              if (tx.bankId && (tx.bankId.toString() === bankId || tx.bankId.toString() === bank._id?.toString())) {
                if (tx.type === 'deposit') {
                  balance += tx.amount || 0;
                } else {
                  balance -= tx.amount || 0;
                }
              }
            });
          } catch (error) {
            console.error(`Error fetching transactions for bank ${bankName} (${bankId}):`, error);
            // Fallback: get all transactions and filter by bankId
            try {
              const allBankTransactionsResponse = await api.get(`/bank/company/transactions/all`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const allBankTransactions = allBankTransactionsResponse.data.transactions || [];

              // Only include transactions for THIS specific bank
              allBankTransactions.forEach(tx => {
                if (tx.bankId) {
                  const txBankId = tx.bankId.toString();
                  if (txBankId === bankId || txBankId === bank._id?.toString()) {
                    if (tx.type === 'deposit') {
                      balance += tx.amount || 0;
                    } else {
                      balance -= tx.amount || 0;
                    }
                  }
                }
              });
            } catch (fallbackError) {
              console.error(`Error in fallback for bank ${bankName}:`, fallbackError);
            }
          }

          // Store balance with multiple keys for easier lookup
          balances[bankId] = balance;
          if (bank._id) {
            const bankIdStr = bank._id.toString();
            balances[bankIdStr] = balance;
            balances[bank._id] = balance; // Also store with ObjectId as key
          }
          // Also store by bank name (for payment method matching)
          if (bankName) {
            balances[bankName] = balance;
          }
          if (bankNameUrdu && bankNameUrdu !== bankName) {
            balances[bankNameUrdu] = balance;
          }
          console.log(`Bank ${bankName} (${bankId}): Balance = ${balance} (from bank's own ledger)`);
        }));
      }

      // Calculate balances for payment methods (like dashboard cards)
      // Get all bank transactions for payment methods (those without bankId)
      const allBankTransactionsResponse = await api.get(`/bank/company/transactions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allBankTransactions = allBankTransactionsResponse.data.transactions || [];

      if (paymentMethods.length > 0) {
        paymentMethods.forEach(paymentMethod => {
          const methodName = paymentMethod.name || '';
          const methodNameUrdu = paymentMethod.nameUrdu || paymentMethod.name || '';
          const methodId = paymentMethod._id?.toString() || paymentMethod._id;

          let balance = paymentMethod.openingBalance || 0;

          // Add deposits (recoveries)
          recoveries.forEach(recovery => {
            const recoveryPaymentMethod = recovery.paymentMethod || '';
            if (recoveryPaymentMethod === methodName ||
              recoveryPaymentMethod === methodNameUrdu ||
              recoveryPaymentMethod === paymentMethod.name ||
              recoveryPaymentMethod === paymentMethod.nameUrdu) {
              balance += recovery.recoveryAmount || 0;
            }
          });

          // Subtract withdrawals (expenses)
          expenses.forEach(expense => {
            const expensePaymentMethod = expense.paymentMethod || '';
            if (expensePaymentMethod === methodName ||
              expensePaymentMethod === methodNameUrdu ||
              expensePaymentMethod === paymentMethod.name ||
              expensePaymentMethod === paymentMethod.nameUrdu) {
              const expenseAmount = (expense.travellingExpense || 0) + (expense.refreshment || 0) +
                (expense.cargo || 0) + (expense.salaryExpense || 0);
              balance -= expenseAmount;
            }
          });

          // Include BankTransactions in balance calculation (only for payment methods, not banks)
          // Only include transactions that don't have bankId (payment method transactions)
          allBankTransactions.forEach(tx => {
            // Skip transactions that belong to a bank ledger (they have bankId)
            if (tx.bankId) {
              return; // Skip bank-specific transactions
            }

            // Only process payment method transactions
            const txPaymentMethod = tx.paymentMethod || '';
            if (txPaymentMethod === methodName ||
              txPaymentMethod === methodNameUrdu ||
              txPaymentMethod === paymentMethod.name ||
              txPaymentMethod === paymentMethod.nameUrdu) {
              if (tx.type === 'deposit') {
                balance += tx.amount || 0;
              } else if (tx.type === 'withdraw') {
                balance -= tx.amount || 0;
              }
            }
          });

          // Store balance with multiple keys for easier lookup
          balances[methodId] = balance;
          if (paymentMethod._id) {
            const methodIdStr = paymentMethod._id.toString();
            balances[methodIdStr] = balance;
            balances[paymentMethod._id] = balance;
          }
          // Store by payment method name
          if (methodName) {
            balances[methodName] = balance;
          }
          if (methodNameUrdu && methodNameUrdu !== methodName) {
            balances[methodNameUrdu] = balance;
          }
          // Also store with paymentMethod_ prefix for column lookup
          balances[`paymentMethod_${methodId}`] = balance;
          if (paymentMethod._id) {
            balances[`paymentMethod_${paymentMethod._id.toString()}`] = balance;
          }
          console.log(`Payment Method ${methodName} (${methodId}): Balance = ${balance}`);
        });
      }

      console.log('Fetched all balances (banks + payment methods):', balances);
      console.log('Balance keys:', Object.keys(balances));
      setBankBalances(balances);
    } catch (error) {
      console.error("Fetch bank balances error:", error);
    }
  };

  // Helper function to fetch balance from ledger for a supplier
  const fetchSupplierBalance = async (supplierPartyId, billId) => {
    try {
      if (!supplierPartyId) return null;

      const token = localStorage.getItem("companyToken");
      const response = await api.get(`/ledger/parties/${supplierPartyId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const transactions = response.data.transactions || [];

      // Find transactions for this bill
      const billTransactions = transactions.filter(tx => {
        const txBillId = tx.billId?._id || (tx.billId && typeof tx.billId === 'object' ? tx.billId._id : tx.billId) || tx.billId;
        return txBillId && txBillId.toString() === billId.toString();
      });

      if (billTransactions.length > 0) {
        // Use balanceAfter from the last transaction for this bill
        billTransactions.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          if (dateA.getTime() === dateB.getTime()) {
            return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
          }
          return dateA - dateB;
        });
        const lastTx = billTransactions[billTransactions.length - 1];
        return parseFloat(lastTx.balanceAfter || 0);
      }

      return null;
    } catch (error) {
      console.error("Error fetching supplier balance:", error);
      return null;
    }
  };

  // Helper function to get settled bill IDs from both localStorage and state
  const getSettledBillIds = () => {
    const settledBillIds = [];

    // Check localStorage
    if (company?._id) {
      const settledKey = `rozSettledSuppliers_${company._id}`;
      const savedSettledSuppliers = localStorage.getItem(settledKey);
      if (savedSettledSuppliers) {
        try {
          const settled = JSON.parse(savedSettledSuppliers);
          settled.forEach(s => {
            const sBillId = s.billId || s.id;
            if (sBillId) {
              const idStr = sBillId.toString();
              if (!settledBillIds.includes(idStr)) {
                settledBillIds.push(idStr);
              }
            }
          });
        } catch (error) {
          console.error("Error parsing settled suppliers from localStorage:", error);
        }
      }
    }

    // Also check settledSuppliers state
    if (settledSuppliers && settledSuppliers.length > 0) {
      settledSuppliers.forEach(s => {
        const sBillId = s.billId || s.id;
        if (sBillId) {
          const idStr = sBillId.toString();
          if (!settledBillIds.includes(idStr)) {
            settledBillIds.push(idStr);
          }
        }
      });
    }

    return settledBillIds;
  };

  const fetchBills = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/bill/company/my-bills", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const billsData = response.data.bills || [];
      setBills(billsData);

      // Get settled bill IDs once
      const settledBillIds = getSettledBillIds();
      console.log("Settled bill IDs to exclude:", settledBillIds);

      // Fetch ledger transactions for all suppliers to get balances
      const supplierTransactionsMap = new Map();
      const supplierParties = ledgerParties.filter(p => p.type === 'supplier');

      // Fetch all supplier transactions in parallel
      await Promise.all(supplierParties.map(async (party) => {
        try {
          const txResponse = await api.get(`/ledger/parties/${party._id}/transactions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const transactions = txResponse.data.transactions || [];
          // Sort transactions by date and creation time
          transactions.sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt || 0);
            const dateB = new Date(b.date || b.createdAt || 0);
            if (dateA.getTime() === dateB.getTime()) {
              return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            }
            return dateA - dateB;
          });
          supplierTransactionsMap.set(party._id.toString(), transactions);
          console.log(`Fetched ${transactions.length} transactions for supplier ${party._id}`);
        } catch (error) {
          console.error(`Error fetching transactions for supplier ${party._id}:`, error);
        }
      }));

      // Populate supplier rows from bills
      const supplierBills = billsData.map(bill => {
        // Get cash amount from expenses (check both English and Urdu keys)
        const cashAmount = parseFloat(bill.expenses?.Cash || bill.expenses?.نقدی || 0);
        // Get netSale (pukhta bikri)
        const netSale = parseFloat(bill.netSale || 0);
        // Calculate sale = pukhta + nakadi
        const sale = netSale + cashAmount;

        // Get phone number from bill or ledger party
        let customerPhone = '';
        if (bill.customerPhone) {
          customerPhone = bill.customerPhone;
        } else if (bill.customerPartyId) {
          // Find party from ledgerParties array
          const party = ledgerParties.find(p => p._id === bill.customerPartyId);
          if (party && party.phone) {
            customerPhone = party.phone;
          }
        }

        // Get vehicle number (گاڑی نمبر) from bill expenses
        const vehicleNumber = bill.expenses?.ActualVehicle || '';

        // Calculate balance from ledger transactions - get balance AFTER this bill
        let balance = 0;
        if (bill.customerPartyId) {
          const supplierParty = ledgerParties.find(p => p._id === bill.customerPartyId && p.type === 'supplier');
          if (supplierParty) {
            const transactions = supplierTransactionsMap.get(bill.customerPartyId.toString()) || [];

            // Calculate balance up to and including this bill's date
            const billDate = new Date(bill.date || bill.createdAt || 0);
            const billCreatedAt = new Date(bill.createdAt || billDate);

            // Find transactions for this bill
            const billTransactions = transactions.filter(tx => {
              const txBillId = tx.billId?._id || (tx.billId && typeof tx.billId === 'object' ? tx.billId._id : tx.billId) || tx.billId;
              return txBillId && txBillId.toString() === bill._id.toString();
            });

            if (billTransactions.length > 0) {
              // Use balanceAfter from the last transaction for this bill
              billTransactions.sort((a, b) => {
                const dateA = new Date(a.date || a.createdAt || 0);
                const dateB = new Date(b.date || b.createdAt || 0);
                if (dateA.getTime() === dateB.getTime()) {
                  return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                }
                return dateA - dateB;
              });
              const lastTx = billTransactions[billTransactions.length - 1];
              balance = parseFloat(lastTx.balanceAfter || 0);
              console.log(`Bill ${bill._id}: Using balanceAfter from transaction: ${balance}`);
            } else {
              // No ledger transactions for this bill, calculate balance up to this bill
              const openingBalance = parseFloat(supplierParty.openingBalance || 0);
              balance = openingBalance;

              // Add all transactions up to and including this bill's date
              for (const tx of transactions) {
                const txDate = new Date(tx.date || tx.createdAt || 0);
                const txCreatedAt = new Date(tx.createdAt || txDate);

                // Include transactions up to this bill (including same date/time)
                if (txDate < billDate || (txDate.getTime() === billDate.getTime() && txCreatedAt <= billCreatedAt)) {
                  if (tx.type === 'debit') {
                    balance += parseFloat(tx.amount || 0);
                  } else if (tx.type === 'credit') {
                    balance -= parseFloat(tx.amount || 0);
                  }
                }
              }

              // Add this bill's net amount (debit for sale amount, credit for cash paid)
              balance += sale; // Debit
              balance -= cashAmount; // Credit
              console.log(`Bill ${bill._id}: Calculated balance manually: opening=${openingBalance}, sale=${sale}, cash=${cashAmount}, final=${balance}`);
            }
          } else {
            // No supplier party found, just show sale - cash
            balance = sale - cashAmount;
          }
        } else {
          // No customerPartyId, just show sale - cash
          balance = sale - cashAmount;
        }

        const row = {
          id: bill._id,
          date: bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
          billNumber: bill.billNumber || bill.billNo || bill.serialNumber || bill._id?.toString().slice(-6) || '', // Bill number
          supplierName: bill.customerPartyId || '', // Use customerPartyId if available
          customerName: bill.customerName || '',
          customerNameUrdu: bill.customerNameUrdu || '',
          customerPhone: customerPhone, // Store phone number
          vehicleNumber: vehicleNumber, // Store vehicle number (گاڑی نمبر)
          sale: sale > 0 ? sale.toFixed(2) : '',
          cash: cashAmount > 0 ? cashAmount.toFixed(2) : '',
          amount: '', // نقد رقم - will be set from dynamic columns
          balance: balance.toFixed(2), // Balance from ledger transactions
          billId: bill._id
        };

        // Add dynamic columns from saved template
        supplierColumns.forEach(col => {
          if (!col.isDefault && col.key) {
            // Try to get value from bill data if available
            row[col.key] = bill[col.key] || '';
            // Note: Don't recalculate balance for 'amount' field if balance is from ledger
            // Balance should remain from ledger transactions
          }
        });

        return row;
      });

      // Only set if we have bills and no saved rows exist
      // Check if saved rows exist in localStorage
      if (company?._id) {
        const supplierKey = `rozSupplierRows_${company._id}`;
        const savedRows = localStorage.getItem(supplierKey);

        if (!savedRows && supplierBills.length > 0) {
          // No saved rows, filter out settled bills
          const activeBills = supplierBills.filter(billRow => {
            const billIdStr = (billRow.billId || billRow.id)?.toString();
            return !settledBillIds.includes(billIdStr);
          });

          // Sort by bill number sequence before setting
          const sortedActiveBills = sortSupplierRowsBySequence(activeBills);
          setSupplierRows(sortedActiveBills);
          // Save bills data to localStorage
          localStorage.setItem(supplierKey, JSON.stringify(sortedActiveBills));
        } else if (savedRows) {
          // Saved rows exist, merge with bills data (update existing rows, add new ones)
          try {
            const saved = JSON.parse(savedRows);
            const savedMap = new Map(saved.map(row => [row.billId || row.id, row]));

            // Update saved rows with bill data, or add new bills (but exclude settled bills)
            supplierBills.forEach(billRow => {
              const billIdStr = (billRow.billId || billRow.id)?.toString();
              // Skip if this bill is already settled
              if (settledBillIds.includes(billIdStr)) {
                return;
              }

              const existing = savedMap.get(billRow.billId || billRow.id);
              if (existing) {
                // Update existing row with bill data (preserve manual edits for bank/payment method columns)
                Object.assign(existing, {
                  date: billRow.date,
                  billNumber: billRow.billNumber, // Update bill number
                  sale: billRow.sale,
                  cash: billRow.cash,
                  customerName: billRow.customerName,
                  customerNameUrdu: billRow.customerNameUrdu,
                  customerPhone: billRow.customerPhone,
                  vehicleNumber: billRow.vehicleNumber
                  // Don't update balance here - it will be recalculated below based on current values
                });

                // Recalculate balance based on current values (sale, cash, banks, payment methods)
                // This ensures balance reflects any manually entered bank/payment method values
                const sale = parseFloat(existing.sale || 0);
                const cash = parseFloat(existing.cash || 0);
                const amount = parseFloat(existing.amount || 0);

                // Sum all bank columns
                let totalBanks = 0;
                supplierColumns.forEach(col => {
                  if (col.key && col.key.startsWith('bank_')) {
                    totalBanks += parseFloat(existing[col.key] || 0);
                  }
                });

                // Sum all payment method columns
                let totalPaymentMethods = 0;
                supplierColumns.forEach(col => {
                  if (col.key && col.key.startsWith('paymentMethod_')) {
                    totalPaymentMethods += parseFloat(existing[col.key] || 0);
                  }
                });

                // Calculate balance: sale minus all payments
                const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amount;
                existing.balance = calculatedBalance.toFixed(2);

                console.log(`Recalculated balance for existing row ${existing.id}: sale=${sale}, cash=${cash}, banks=${totalBanks}, paymentMethods=${totalPaymentMethods}, amount=${amount}, balance=${calculatedBalance.toFixed(2)}`);
              } else {
                // Add new bill row (only if not settled)
                const billIdStr = (billRow.billId || billRow.id)?.toString();
                if (!settledBillIds.includes(billIdStr)) {
                  savedMap.set(billRow.billId || billRow.id, billRow);
                }
              }
            });

            // Filter out settled bills from merged array
            const merged = Array.from(savedMap.values()).filter(row => {
              const rowBillId = (row.billId || row.id)?.toString();
              return !settledBillIds.includes(rowBillId);
            });

            // Sort by bill number sequence before setting
            const sortedMerged = sortSupplierRowsBySequence(merged);
            setSupplierRows(sortedMerged);
            localStorage.setItem(supplierKey, JSON.stringify(sortedMerged));
          } catch (error) {
            console.error("Error merging rows:", error);
            // Even on error, filter out settled bills
            if (supplierBills.length > 0) {
              const activeBills = supplierBills.filter(billRow => {
                const billIdStr = (billRow.billId || billRow.id)?.toString();
                return !settledBillIds.includes(billIdStr);
              });
              // Sort by bill number sequence before setting
              const sortedActiveBills = sortSupplierRowsBySequence(activeBills);
              setSupplierRows(sortedActiveBills);
              localStorage.setItem(supplierKey, JSON.stringify(sortedActiveBills));
            }
          }
        }
      } else if (supplierBills.length > 0) {
        // Filter out settled bills
        const activeBills = supplierBills.filter(billRow => {
          const billIdStr = (billRow.billId || billRow.id)?.toString();
          return !settledBillIds.includes(billIdStr);
        });

        setSupplierRows(activeBills);
      }
    } catch (error) {
      console.error("Fetch bills error:", error);
      toast.error(companyLanguage === 'ur' ? 'بلز لوڈ کرنے میں ناکام' : 'Failed to load bills');
    }
  };

  const fetchSales = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/sale/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const salesData = response.data.sales || [];
      console.log("Fetched sales data:", salesData.length, salesData);

      // Populate customer rows from sales
      if (company?._id) {
        const customerKey = `rozCustomerRows_${company._id}`;
        const savedRows = localStorage.getItem(customerKey);

        // Sort sales by date to calculate running balance
        const sortedSales = [...salesData].sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateA - dateB;
        });

        // Fetch ledger transactions for all customers to calculate accurate balances
        const customerTransactionsMap = new Map();
        const customerParties = ledgerParties.filter(p => p.type === 'customer');

        // Fetch all transactions in parallel
        await Promise.all(customerParties.map(async (party) => {
          try {
            const txResponse = await api.get(`/ledger/parties/${party._id}/transactions`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const transactions = txResponse.data.transactions || [];
            // Sort transactions by date and creation time
            transactions.sort((a, b) => {
              const dateA = new Date(a.date || a.createdAt || 0);
              const dateB = new Date(b.date || b.createdAt || 0);
              if (dateA.getTime() === dateB.getTime()) {
                return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
              }
              return dateA - dateB;
            });
            customerTransactionsMap.set(party._id.toString(), transactions);
            console.log(`Fetched ${transactions.length} transactions for customer ${party._id}`);
          } catch (error) {
            console.error(`Error fetching transactions for party ${party._id}:`, error);
          }
        }));

        // Create rows from sales with running balance based on ledger transactions
        const salesRows = sortedSales.map((sale, index) => {
          const saleAmount = parseFloat(sale.totalAmount || 0);
          const amountPaid = parseFloat(sale.amountPaid || 0);

          let balance = 0;

          // If customer is selected, calculate balance from ledger transactions
          if (sale.customerPartyId) {
            const customerParty = ledgerParties.find(p => p._id === sale.customerPartyId);
            if (customerParty) {
              const openingBalance = parseFloat(customerParty.openingBalance || 0);
              const transactions = customerTransactionsMap.get(sale.customerPartyId.toString()) || [];

              // Calculate balance up to this sale's date
              const saleDate = new Date(sale.date || sale.createdAt || 0);
              const saleCreatedAt = new Date(sale.createdAt || saleDate);

              // Find transactions for this sale
              const saleTransactions = transactions.filter(tx => {
                const txSaleId = tx.saleId?._id || (tx.saleId && typeof tx.saleId === 'object' ? tx.saleId._id : tx.saleId) || tx.saleId;
                return txSaleId && txSaleId.toString() === sale._id.toString();
              });

              if (saleTransactions.length > 0) {
                // Use balanceAfter from the last transaction for this sale
                saleTransactions.sort((a, b) => {
                  const dateA = new Date(a.date || a.createdAt || 0);
                  const dateB = new Date(b.date || b.createdAt || 0);
                  if (dateA.getTime() === dateB.getTime()) {
                    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                  }
                  return dateA - dateB;
                });
                const lastTx = saleTransactions[saleTransactions.length - 1];
                balance = parseFloat(lastTx.balanceAfter || 0);
              } else {
                // No ledger transactions for this sale, calculate balance manually
                balance = openingBalance;

                // Add all transactions before this sale
                for (const tx of transactions) {
                  const txDate = new Date(tx.date || tx.createdAt || 0);
                  const txCreatedAt = new Date(tx.createdAt || txDate);

                  if (txDate < saleDate || (txDate.getTime() === saleDate.getTime() && txCreatedAt < saleCreatedAt)) {
                    if (tx.type === 'debit') {
                      balance += parseFloat(tx.amount || 0);
                    } else if (tx.type === 'credit') {
                      balance -= parseFloat(tx.amount || 0);
                    }
                  }
                }

                // Add this sale's net amount (debit for sale amount, credit for amount paid)
                balance += saleAmount; // Debit
                balance -= amountPaid; // Credit
                console.log(`Sale ${sale._id}: Calculated balance manually: opening=${openingBalance}, sale=${saleAmount}, paid=${amountPaid}, final=${balance}`);
              }
            } else {
              console.log(`Sale ${sale._id}: Customer party not found`);
            }
          } else {
            // No customer selected, just show sale amount - amount paid
            balance = saleAmount - amountPaid;
            console.log(`Sale ${sale._id}: No customer, balance=${balance}`);
          }

          console.log(`Sale ${sale._id}: Final balance=${balance}`);

          // Format supplier name with vehicle number
          const supplierName = sale.supplierNameUrdu || sale.supplierName || '';
          const vehicleNumber = sale.supplierVehicleNumber || '';
          const supplierNameWithVehicle = vehicleNumber
            ? `${supplierName} - گاڑی نمبر: ${vehicleNumber}`
            : supplierName;

          // Get receipt number - EXACT same logic as SaleView.jsx line 184: sale.serialNumber || saleId.slice(-6)
          // This is the receipt number from the sale receipt
          const receiptNum = sale.serialNumber || sale._id?.toString().slice(-6) || '';

          console.log("Sale data - sale.serialNumber:", sale.serialNumber, "sale._id:", sale._id, "receiptNum (final):", receiptNum);

          // Calculate initial balance: sale - cash (this matches receipt balance)
          // The balance from ledger might be cumulative, but for this row we need: sale - cash
          const initialBalance = saleAmount - amountPaid;

          // Use ledger balance if available and valid, otherwise use sale - cash
          const finalBalance = (balance && balance !== 0 && !isNaN(balance)) ? balance : initialBalance;

          const row = {
            id: `sale_${sale._id}`,
            date: sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
            receiptNumber: receiptNum, // This should be the receipt number from sale receipt
            supplierName: supplierNameWithVehicle,
            supplierNameOriginal: supplierName, // Store original name separately
            vehicleNumber: vehicleNumber, // Store vehicle number separately
            customerName: sale.sellerNameUrdu || sale.sellerName || '',
            customerNameUrdu: sale.sellerNameUrdu || sale.sellerName || '',
            sale: saleAmount > 0 ? saleAmount.toFixed(2) : '',
            cash: amountPaid > 0 ? amountPaid.toFixed(2) : '',
            balance: finalBalance.toFixed(2), // Use sale - cash for initial balance (matches receipt)
            saleId: sale._id,
            customerPartyId: sale.customerPartyId || null,
            isFromSale: true
          };

          console.log(`Sale ${sale._id}: saleAmount=${saleAmount}, amountPaid=${amountPaid}, initialBalance=${initialBalance}, ledgerBalance=${balance}, finalBalance=${finalBalance}`);

          console.log("Created row from sale - sale.serialNumber:", sale.serialNumber, "sale._id:", sale._id, "receiptNumber:", receiptNum, "full row:", row);

          // Add dynamic columns from saved template
          customerColumns.forEach(col => {
            if (!col.isDefault && col.key) {
              row[col.key] = sale[col.key] || '';
            }
          });

          return row;
        });

        if (!savedRows && salesRows.length > 0) {
          // No saved rows, use sales data
          console.log("No saved rows, setting sales rows:", salesRows);
          setCustomerRows(salesRows);
          localStorage.setItem(customerKey, JSON.stringify(salesRows));
          console.log("Saved sales rows to localStorage:", salesRows);
        } else if (savedRows) {
          // Merge sales with saved rows
          try {
            const saved = JSON.parse(savedRows);
            const savedMap = new Map(saved.map(row => [row.saleId || row.id, row]));

            // Add or update sales rows
            salesRows.forEach(saleRow => {
              const saleId = saleRow.saleId || saleRow.id;
              const existing = savedMap.get(saleId);
              if (existing) {
                // Update existing row with sale data (preserve manual edits for amount, etc.)
                // Force update receiptNumber and supplierName from sale data - always use saleRow data
                existing.date = saleRow.date;
                // Force update receiptNumber - use saleRow value, fallback to saleId
                existing.receiptNumber = saleRow.receiptNumber || (saleRow.saleId ? saleRow.saleId.toString().slice(-6) : '') || '';
                existing.supplierName = saleRow.supplierName || '';
                existing.supplierNameOriginal = saleRow.supplierNameOriginal || '';
                existing.vehicleNumber = saleRow.vehicleNumber || '';
                existing.customerName = saleRow.customerName || existing.customerName;
                existing.customerNameUrdu = saleRow.customerNameUrdu || existing.customerNameUrdu;
                existing.sale = saleRow.sale || existing.sale;
                existing.cash = saleRow.cash || existing.cash;
                existing.balance = saleRow.balance || existing.balance;
                existing.isFromSale = true;
                existing.saleId = saleRow.saleId;
                console.log("Updated existing row - saleRow.receiptNumber:", saleRow.receiptNumber, "final existing.receiptNumber:", existing.receiptNumber, "saleId:", saleRow.saleId);
              } else {
                // Add new sale row
                savedMap.set(saleId, saleRow);
                console.log("Added new sale row - receiptNumber:", saleRow.receiptNumber, "saleId:", saleRow.saleId);
              }
            });

            const merged = Array.from(savedMap.values());
            console.log("Merged customer rows - total:", merged.length);
            merged.forEach((r, idx) => {
              if (r.isFromSale) {
                console.log(`Row ${idx} - receiptNumber:`, r.receiptNumber, "saleId:", r.saleId, "isFromSale:", r.isFromSale);
              }
            });
            setCustomerRows(merged);
            localStorage.setItem(customerKey, JSON.stringify(merged));
          } catch (error) {
            console.error("Error merging sales:", error);
          }
        }
      }
    } catch (error) {
      console.error("Fetch sales error:", error);
    }
  };

  const handleSupplierRowChange = async (id, field, value) => {
    // NOTE: Removed optimistic bank balance update to prevent double deduction
    // Bank balance will be updated only after transaction is created in handleSave
    // This prevents the issue where balance gets subtracted multiple times

    setSupplierRows(prevRows => {
      const updatedRows = prevRows.map(row => {
        if (row.id === id) {
          const oldValue = parseFloat(row[field] || 0);
          const newValue = parseFloat(value || 0);
          const updatedRow = { ...row, [field]: value };

          // Initialize _saved_ value for bank fields if it doesn't exist
          // This ensures proper tracking of what was already saved
          if (field.startsWith('bank_') && updatedRow[`_saved_${field}`] === undefined) {
            updatedRow[`_saved_${field}`] = oldValue; // Set to old value so difference calculation works correctly
          }

          // Note: Bank transaction creation is now handled in handleSave only
          // This ensures transactions are only created once when save is clicked
          // Bank balance will be updated after transaction is created (no optimistic update)

          // Auto-calculate balance: sale - cash - all banks - all payment methods - amount (نقد رقم)
          // Always recalculate balance when bank/payment method values change, even for bill rows
          // This ensures that when user manually enters bank amounts, balance is updated
          if (field === 'sale' || field === 'cash' || field === 'amount' || field.startsWith('bank_') || field.startsWith('paymentMethod_')) {
            const sale = parseFloat(updatedRow.sale || 0);
            const cash = parseFloat(updatedRow.cash || 0);
            const amount = parseFloat(updatedRow.amount || 0); // نقد رقم

            // Sum all bank columns
            let totalBanks = 0;
            supplierColumns.forEach(col => {
              if (col.key && col.key.startsWith('bank_')) {
                totalBanks += parseFloat(updatedRow[col.key] || 0);
              }
            });

            // Sum all payment method columns
            let totalPaymentMethods = 0;
            supplierColumns.forEach(col => {
              if (col.key && col.key.startsWith('paymentMethod_')) {
                totalPaymentMethods += parseFloat(updatedRow[col.key] || 0);
              }
            });

            // Calculate balance: sale minus all payments (cash, banks, payment methods, amount)
            const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amount;
            updatedRow.balance = calculatedBalance.toFixed(2);

            console.log(`Balance recalculated for row ${id}: sale=${sale}, cash=${cash}, banks=${totalBanks}, paymentMethods=${totalPaymentMethods}, amount=${amount}, balance=${calculatedBalance.toFixed(2)}`);
          }

          // Initialize dynamic columns if they don't exist
          supplierColumns.forEach(col => {
            if (!col.isDefault && col.key && !(col.key in updatedRow)) {
              updatedRow[col.key] = '';
            }
          });

          return updatedRow;
        }
        return row;
      });

      // Check for auto-settlement after balance update
      const updatedRow = updatedRows.find(row => row.id === id);
      if (updatedRow) {
        const balance = parseFloat(updatedRow.balance || 0);
        if (Math.abs(balance) < 0.01 && (updatedRow.billId || updatedRow.id)) {
          // Bill is fully paid, automatically move to settled list
          const billId = updatedRow.billId || updatedRow.id;
          const supplierName = updatedRow.supplierName || '';

          // Move to settled list
          setSettledSuppliers(prev => {
            const alreadySettled = prev.find(s => {
              const sBillId = s.billId || s.id;
              return sBillId && billId && (sBillId.toString() === billId.toString());
            });
            if (alreadySettled) {
              return prev;
            }

            const newSettled = [...prev, { ...updatedRow, settledAt: new Date().toISOString() }];

            // Sort by bill number sequence
            const sortedSettled = sortSupplierRowsBySequence(newSettled);

            // Save to localStorage
            if (company?._id) {
              try {
                const settledKey = `rozSettledSuppliers_${company._id}`;
                localStorage.setItem(settledKey, JSON.stringify(sortedSettled));
              } catch (error) {
                console.error("Error saving settled suppliers:", error);
              }
            }

            return sortedSettled;
          });

          // Remove from active list
          const filteredRows = updatedRows.filter(r => {
            const rBillId = r.billId || r.id;
            return !(billId && rBillId && (rBillId.toString() === billId.toString()));
          });

          // Save filtered rows to localStorage
          if (company?._id) {
            try {
              const supplierKey = `rozSupplierRows_${company._id}`;
              localStorage.setItem(supplierKey, JSON.stringify(filteredRows));
            } catch (error) {
              console.error("Error saving supplier rows:", error);
            }
          }

          toast.success(companyLanguage === 'ur'
            ? `بل خود بخود settled ہو گیا - ${supplierName}`
            : `Bill automatically settled - ${supplierName}`);

          return filteredRows;
        }
      }

      // Save to localStorage after state update
      setTimeout(() => {
        if (company?._id) {
          try {
            const supplierKey = `rozSupplierRows_${company._id}`;
            localStorage.setItem(supplierKey, JSON.stringify(updatedRows));
          } catch (error) {
            console.error("Error saving supplier rows:", error);
          }
        }
      }, 0);

      // Trigger auto-save after field change
      triggerAutoSave('supplier');

      return updatedRows;
    });
  };

  const handleCustomerRowChange = async (id, field, value) => {
    // NOTE: Removed optimistic bank balance update to prevent double deduction
    // Bank balance will be updated only after transaction is created in handleSave
    // This prevents the issue where balance gets subtracted multiple times

    setCustomerRows(prevRows => {
      const updatedRows = prevRows.map(row => {
        if (row.id === id) {
          const oldValue = parseFloat(row[field] || 0);
          const newValue = parseFloat(value || 0);
          const updatedRow = { ...row, [field]: value };

          // Initialize _saved_ value for bank fields if it doesn't exist
          // This ensures proper tracking of what was already saved
          if (field.startsWith('bank_') && updatedRow[`_saved_${field}`] === undefined) {
            updatedRow[`_saved_${field}`] = oldValue; // Set to old value so difference calculation works correctly
          }

          // Note: Bank transaction creation is now handled in handleSave only
          // This ensures transactions are only created once when save is clicked
          // Bank balance will be updated after transaction is created (no optimistic update)

          // Auto-calculate balance: sale - cash - all banks - all payment methods - amount (نقد رقم)
          // Always recalculate balance when bank/payment method values change
          // This ensures that when user manually enters bank amounts, balance is updated
          // BUT: If row is from sale and has initial balance from ledger, preserve it unless user makes changes
          if (field === 'sale' || field === 'cash' || field === 'amount' || field.startsWith('bank_') || field.startsWith('paymentMethod_')) {
            const sale = parseFloat(updatedRow.sale || 0);
            const cash = parseFloat(updatedRow.cash || 0);
            const amount = parseFloat(updatedRow.amount || 0); // نقد رقم

            // Sum all bank columns
            let totalBanks = 0;
            customerColumns.forEach(col => {
              if (col.key && col.key.startsWith('bank_')) {
                totalBanks += parseFloat(updatedRow[col.key] || 0);
              }
            });

            // Sum all payment method columns
            let totalPaymentMethods = 0;
            customerColumns.forEach(col => {
              if (col.key && col.key.startsWith('paymentMethod_')) {
                totalPaymentMethods += parseFloat(updatedRow[col.key] || 0);
              }
            });

            // Calculate balance: sale minus all payments (cash, banks, payment methods, amount)
            const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amount;

            // Always recalculate balance when user makes changes
            // This ensures balance is always accurate based on current values
            updatedRow.balance = calculatedBalance.toFixed(2);

            console.log(`Balance recalculated for row ${id}: sale=${sale}, cash=${cash}, banks=${totalBanks}, paymentMethods=${totalPaymentMethods}, amount=${amount}, balance=${calculatedBalance.toFixed(2)}`);
          }

          // Initialize dynamic columns if they don't exist
          customerColumns.forEach(col => {
            if (!col.isDefault && col.key && !(col.key in updatedRow)) {
              updatedRow[col.key] = '';
            }
          });

          return updatedRow;
        }
        return row;
      });

      // Save to localStorage after state update
      setTimeout(() => {
        if (company?._id) {
          try {
            const customerKey = `rozCustomerRows_${company._id}`;
            localStorage.setItem(customerKey, JSON.stringify(updatedRows));
          } catch (error) {
            console.error("Error saving customer rows:", error);
          }
        }
      }, 0);

      // Trigger auto-save after field change
      triggerAutoSave('customer');

      return updatedRows;
    });
  };

  const handleAddSupplierRow = () => {
    const newRow = {
      id: Date.now() + Math.random(),
      date: new Date().toLocaleDateString('en-CA'),
      supplierName: '',
      sale: '',
      cash: '',
      balance: ''
    };

    // Add dynamic columns (banks)
    supplierColumns.forEach(col => {
      if (!col.isDefault && col.key) {
        newRow[col.key] = '';
      }
    });

    setSupplierRows(prev => {
      const updated = [...prev, newRow];
      // Save to localStorage
      if (company?._id) {
        try {
          const supplierKey = `rozSupplierRows_${company._id}`;
          localStorage.setItem(supplierKey, JSON.stringify(updated));
        } catch (error) {
          console.error("Error saving supplier rows:", error);
        }
      }
      return updated;
    });
  };

  const handleAddCustomerRow = () => {
    const newRow = {
      id: Date.now() + Math.random(),
      date: new Date().toLocaleDateString('en-CA'),
      receiptNumber: '',
      supplierName: '',
      customerName: '',
      sale: '',
      cash: '',
      balance: ''
    };

    // Add dynamic columns (banks)
    customerColumns.forEach(col => {
      if (!col.isDefault && col.key) {
        newRow[col.key] = '';
      }
    });

    setCustomerRows(prev => {
      const updated = [...prev, newRow];
      // Save to localStorage
      if (company?._id) {
        try {
          const customerKey = `rozCustomerRows_${company._id}`;
          localStorage.setItem(customerKey, JSON.stringify(updated));
        } catch (error) {
          console.error("Error saving customer rows:", error);
        }
      }
      return updated;
    });
  };

  const handleDeleteRow = (id, type) => {
    if (type === 'supplier') {
      setSupplierRows(prev => {
        const updated = prev.filter(row => row.id !== id);
        // Save to localStorage
        if (company?._id) {
          try {
            const supplierKey = `rozSupplierRows_${company._id}`;
            localStorage.setItem(supplierKey, JSON.stringify(updated));
          } catch (error) {
            console.error("Error saving supplier rows:", error);
          }
        }
        return updated;
      });
    } else {
      setCustomerRows(prev => {
        const updated = prev.filter(row => row.id !== id);
        // Save to localStorage
        if (company?._id) {
          try {
            const customerKey = `rozCustomerRows_${company._id}`;
            localStorage.setItem(customerKey, JSON.stringify(updated));
          } catch (error) {
            console.error("Error saving customer rows:", error);
          }
        }
        return updated;
      });
    }
  };

  // Auto-save function (debounced)
  const triggerAutoSave = (type) => {
    // Clear existing timer
    if (type === 'supplier' && supplierAutoSaveTimer.current) {
      clearTimeout(supplierAutoSaveTimer.current);
    } else if (type === 'customer' && customerAutoSaveTimer.current) {
      clearTimeout(customerAutoSaveTimer.current);
    }

    // Set new timer (1 second delay)
    const timer = setTimeout(() => {
      handleSave(type, true); // true = silent save (no toast messages)
    }, 1000);

    if (type === 'supplier') {
      supplierAutoSaveTimer.current = timer;
    } else {
      customerAutoSaveTimer.current = timer;
    }
  };

  // Handle payment method payment from table row
  const handlePaymentMethodPaymentFromTable = async (rowId, colKey, paymentMethod, amount, supplierId) => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      if (amount <= 0) {
        return; // Don't create transaction for zero or negative amounts
      }

      if (!supplierId) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم سپلائر منتخب کریں' : 'Please select supplier');
        return;
      }

      // Find supplier party
      const supplierParty = ledgerParties.find(p => {
        const partyId = p._id?.toString() || p._id;
        return partyId === supplierId || partyId?.toString() === supplierId;
      });

      if (!supplierParty) {
        toast.error(companyLanguage === 'ur' ? 'سپلائر نہیں ملا' : 'Supplier not found');
        return;
      }

      const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);

      // Get bill number from row
      const row = supplierRows.find(r => r.id === rowId || r.billId === rowId);
      const billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';

      // Create payment method transaction (withdraw from payment method)
      const payload = {
        paymentMethodId: paymentMethod._id,
        date: new Date().toLocaleDateString('en-CA'),
        type: 'withdraw',
        amount: amount,
        description: companyLanguage === 'ur'
          ? `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
          : `Roznamacha - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - Amount: ${amount.toFixed(2)}`,
        descriptionUrdu: `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
      };

      console.log('Creating payment method transaction from table:', payload);
      await api.post(`/bank/company/payment-method/transaction`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create ledger transaction (debit/sale to supplier - goes to sale field)
      const ledgerPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'debit', // Debit (sale) - goes to sale field in supplier khata
        amount: amount,
        description: companyLanguage === 'ur'
          ? `ادائیگی - ${paymentMethod.nameUrdu || paymentMethod.name}`
          : `Payment - ${paymentMethod.name || paymentMethod.nameUrdu}`,
        descriptionUrdu: `ادائیگی - ${paymentMethod.nameUrdu || paymentMethod.name}`
      };

      console.log('Creating ledger transaction for supplier:', supplierId, ledgerPayload);
      await api.post(`/ledger/parties/${supplierId}/transactions`, ledgerPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh bank balances
      await fetchBankBalances();

      // Refresh ledger parties to get updated balances
      await fetchLedgerParties();

      toast.success(companyLanguage === 'ur'
        ? `ادائیگی لین دین بنایا گیا - ${supplierName}`
        : `Payment transaction created - ${supplierName}`);
    } catch (error) {
      console.error('Error creating payment method payment from table:', error);
      toast.error(companyLanguage === 'ur'
        ? 'ادائیگی بنانے میں ناکام'
        : 'Failed to create payment');
    }
  };

  // Handle bank payment from table row
  const handleBankPaymentFromTable = async (rowId, colKey, bank, amount, supplierId) => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      if (amount <= 0) {
        return; // Don't create transaction for zero or negative amounts
      }

      if (!supplierId) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم سپلائر منتخب کریں' : 'Please select supplier');
        return;
      }

      // Find supplier party
      const supplierParty = ledgerParties.find(p => {
        const partyId = p._id?.toString() || p._id;
        return partyId === supplierId || partyId?.toString() === supplierId;
      });

      if (!supplierParty) {
        toast.error(companyLanguage === 'ur' ? 'سپلائر نہیں ملا' : 'Supplier not found');
        return;
      }

      const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);

      // Get bill number from row
      const row = supplierRows.find(r => r.id === rowId || r.billId === rowId);
      const billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';

      // Create bank transaction (withdraw from bank)
      const bankId = bank._id?.toString() || bank._id;
      const transactionPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'withdraw',
        amount: amount,
        paymentMethod: bank.bankNameUrdu || bank.bankName || '',
        description: companyLanguage === 'ur'
          ? `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
          : `Roznamacha - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - Amount: ${amount.toFixed(2)}`,
        descriptionUrdu: `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
      };

      console.log('Creating bank transaction from table:', transactionPayload);
      await api.post(`/bank/company/${bankId}/transaction`, transactionPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create ledger transaction (debit/sale to supplier - goes to sale field)
      const ledgerPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'debit', // Debit (sale) - goes to sale field in supplier khata
        amount: amount,
        description: companyLanguage === 'ur'
          ? `بینک ادائیگی - ${bank.bankNameUrdu || bank.bankName}`
          : `Bank Payment - ${bank.bankName || bank.bankNameUrdu}`,
        descriptionUrdu: `بینک ادائیگی - ${bank.bankNameUrdu || bank.bankName}`
      };

      console.log('Creating ledger transaction for supplier:', supplierId, ledgerPayload);
      await api.post(`/ledger/parties/${supplierId}/transactions`, ledgerPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh bank balances
      await fetchBankBalances();

      // Refresh ledger parties to get updated balances
      await fetchLedgerParties();

      toast.success(companyLanguage === 'ur'
        ? `بینک لین دین بنایا گیا - ${supplierName}`
        : `Bank transaction created - ${supplierName}`);
    } catch (error) {
      console.error('Error creating bank payment from table:', error);
      toast.error(companyLanguage === 'ur'
        ? 'بینک ادائیگی بنانے میں ناکام'
        : 'Failed to create bank payment');
    }
  };

  // Handle payment method payment from top fields
  const handlePaymentMethodPayment = async (pmFieldKey, paymentMethod, fieldData) => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      const amount = parseFloat(fieldData.amount || 0);
      const supplierId = selectedSupplierId; // Use shared supplier

      if (amount <= 0) {
        toast.error(companyLanguage === 'ur' ? 'رقم درست نہیں' : 'Invalid amount');
        return;
      }

      if (!supplierId) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم سپلائر منتخب کریں' : 'Please select supplier');
        return;
      }

      // Find supplier party
      const supplierParty = ledgerParties.find(p => {
        const partyId = p._id?.toString() || p._id;
        return partyId === supplierId || partyId?.toString() === supplierId;
      });

      if (!supplierParty) {
        toast.error(companyLanguage === 'ur' ? 'سپلائر نہیں ملا' : 'Supplier not found');
        return;
      }

      const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);

      // Get bill number from selected bill row or first matching supplier row
      let billNumber = '';
      if (selectedBillRowId) {
        const row = supplierRows.find(r => r.id === selectedBillRowId || r.billId === selectedBillRowId);
        billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';
      } else {
        // Find first matching supplier row
        const row = supplierRows.find(r => {
          const rowSupplierId = r.supplierName?.toString() || r.supplierName;
          return rowSupplierId === supplierId || rowSupplierId?.toString() === supplierId;
        });
        billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';
      }

      // Create payment method transaction (withdraw from payment method)
      const payload = {
        paymentMethodId: paymentMethod._id,
        date: new Date().toLocaleDateString('en-CA'),
        type: 'withdraw',
        amount: amount,
        description: companyLanguage === 'ur'
          ? `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
          : `Roznamacha - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - Amount: ${amount.toFixed(2)}`,
        descriptionUrdu: `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
      };

      console.log('Creating payment method transaction:', payload);
      await api.post(`/bank/company/payment-method/transaction`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create ledger transaction (debit/sale to supplier - goes to sale field)
      const ledgerPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'debit', // Debit (sale) - goes to sale field in supplier khata
        amount: amount,
        description: companyLanguage === 'ur'
          ? `ادائیگی - ${paymentMethod.nameUrdu || paymentMethod.name}`
          : `Payment - ${paymentMethod.name || paymentMethod.nameUrdu}`,
        descriptionUrdu: `ادائیگی - ${paymentMethod.nameUrdu || paymentMethod.name}`
      };

      console.log('Creating ledger transaction for supplier:', supplierId, ledgerPayload);
      await api.post(`/ledger/parties/${supplierId}/transactions`, ledgerPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update supplier row in table - find the specific bill being worked on
      // If multiple bills exist for same supplier, work on the one with most recent activity
      // Update supplier row in table - find the specific bill being worked on
      // If a specific bill row is selected, use that; otherwise find the one with most recent activity
      setSupplierRows(prevRows => {
        // If a specific bill row is selected, use that
        let supplierRow = null;
        if (selectedBillRowId) {
          supplierRow = prevRows.find(row => row.id === selectedBillRowId || row.billId === selectedBillRowId);
        }

        // If not found or not selected, find all rows for this supplier
        if (!supplierRow) {
          const supplierRows = prevRows.filter(row => {
            const rowSupplierId = row.supplierName?.toString() || row.supplierName;
            return rowSupplierId === supplierId || rowSupplierId?.toString() === supplierId;
          });

          // If multiple bills, find the one with most recent payment activity
          if (supplierRows.length > 1) {
            // Find bill with most recent payment activity (has bank/payment method values)
            const billsWithPayments = supplierRows.filter(row => {
              let hasPayments = false;
              supplierColumns.forEach(col => {
                if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
                  const value = parseFloat(row[col.key] || 0);
                  if (value > 0.01) {
                    hasPayments = true;
                  }
                }
              });
              return hasPayments;
            });

            if (billsWithPayments.length > 0) {
              // Use the bill with most payments (highest total)
              supplierRow = billsWithPayments.reduce((max, row) => {
                let maxTotal = 0;
                let rowTotal = 0;
                supplierColumns.forEach(col => {
                  if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
                    maxTotal += parseFloat(max[col.key] || 0);
                    rowTotal += parseFloat(row[col.key] || 0);
                  }
                });
                return rowTotal > maxTotal ? row : max;
              });
            } else {
              // No payments yet, use the oldest bill (earliest date or lowest billId)
              supplierRow = supplierRows.sort((a, b) => {
                const dateA = new Date(a.date || 0);
                const dateB = new Date(b.date || 0);
                if (dateA.getTime() !== dateB.getTime()) {
                  return dateA - dateB;
                }
                // If same date, use billId
                const idA = (a.billId || a.id)?.toString() || '';
                const idB = (b.billId || b.id)?.toString() || '';
                return idA.localeCompare(idB);
              })[0];
            }
          } else if (supplierRows.length === 1) {
            supplierRow = supplierRows[0];
          }
        }

        if (!supplierRow) {
          // Create new row for this supplier
          supplierRow = {
            id: Date.now() + Math.random(),
            date: new Date().toLocaleDateString('en-CA'),
            supplierName: supplierId,
            sale: '',
            cash: '',
            balance: ''
          };

          // Add dynamic columns
          supplierColumns.forEach(col => {
            if (!col.isDefault && col.key) {
              supplierRow[col.key] = '';
            }
          });

          prevRows = [...prevRows, supplierRow];
        }

        // Helper function to update a row with payment method payment
        const updateRowWithPaymentMethod = (row, pmColumnKey, amount) => {
          const updatedRow = { ...row };
          updatedRow[pmColumnKey] = (parseFloat(updatedRow[pmColumnKey] || 0) + amount).toFixed(2);

          // Recalculate balance
          const sale = parseFloat(updatedRow.sale || 0);
          const cash = parseFloat(updatedRow.cash || 0);
          const amountField = parseFloat(updatedRow.amount || 0);

          // Sum all bank columns
          let totalBanks = 0;
          supplierColumns.forEach(col => {
            if (col.key && col.key.startsWith('bank_')) {
              totalBanks += parseFloat(updatedRow[col.key] || 0);
            }
          });

          // Sum all payment method columns
          let totalPaymentMethods = 0;
          supplierColumns.forEach(col => {
            if (col.key && col.key.startsWith('paymentMethod_')) {
              totalPaymentMethods += parseFloat(updatedRow[col.key] || 0);
            }
          });

          // Calculate balance: sale minus all payments
          const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amountField;
          updatedRow.balance = calculatedBalance.toFixed(2);

          return updatedRow;
        };

        // Get the billId of the bill that was updated (before updating)
        const updatedBillId = supplierRow.billId || supplierRow.id;

        // Update payment method column in the row
        const pmColumnKey = `paymentMethod_${paymentMethod._id}`;
        let updatedBillRow = null;
        const updatedRows = prevRows.map(row => {
          if (row.id === supplierRow.id) {
            updatedBillRow = updateRowWithPaymentMethod(row, pmColumnKey, amount);
            return updatedBillRow;
          }
          return row;
        });

        // Check if bill should be auto-settled (balance <= 0.01)
        if (updatedBillRow) {
          const balance = parseFloat(updatedBillRow.balance || 0);
          if (Math.abs(balance) < 0.01) {
            // Bill is fully paid, automatically move to settled list
            const billId = updatedBillRow.billId || updatedBillRow.id;
            const supplierName = updatedBillRow.supplierName || supplierId;

            // Move to settled list
            setSettledSuppliers(prev => {
              const alreadySettled = prev.find(s => {
                const sBillId = s.billId || s.id;
                return sBillId && billId && (sBillId.toString() === billId.toString());
              });
              if (alreadySettled) {
                return prev;
              }

              const newSettled = [...prev, { ...updatedBillRow, settledAt: new Date().toISOString() }];

              // Save to localStorage
              if (company?._id) {
                try {
                  const settledKey = `rozSettledSuppliers_${company._id}`;
                  localStorage.setItem(settledKey, JSON.stringify(newSettled));
                } catch (error) {
                  console.error("Error saving settled suppliers:", error);
                }
              }

              return newSettled;
            });

            // Remove from active list
            const filteredRows = updatedRows.filter(row => {
              const rowBillId = row.billId || row.id;
              return !(billId && rowBillId && (rowBillId.toString() === billId.toString()));
            });

            // Save filtered rows to localStorage
            if (company?._id) {
              try {
                const supplierKey = `rozSupplierRows_${company._id}`;
                localStorage.setItem(supplierKey, JSON.stringify(filteredRows));
              } catch (error) {
                console.error("Error saving supplier rows:", error);
              }
            }

            toast.success(companyLanguage === 'ur'
              ? `بل خود بخود settled ہو گیا - ${supplierName}`
              : `Bill automatically settled - ${supplierName}`);

            return filteredRows;
          }
        }

        // Save to localStorage
        if (company?._id) {
          try {
            const supplierKey = `rozSupplierRows_${company._id}`;
            localStorage.setItem(supplierKey, JSON.stringify(updatedRows));
          } catch (error) {
            console.error("Error saving supplier rows:", error);
          }
        }

        // Also update settled suppliers - update ONLY the specific bill that was updated (by billId)
        setSettledSuppliers(prevSettled => {
          // Find the specific bill in settled list by billId (not just supplierId)
          const settledBill = prevSettled.find(s => {
            const sBillId = s.billId || s.id;
            return sBillId && updatedBillId && (sBillId.toString() === updatedBillId.toString());
          });

          if (settledBill) {
            console.log('Updating specific settled bill (payment method) by billId:', updatedBillId, 'pm column:', pmColumnKey, 'with amount:', amount);

            const updatedSettled = prevSettled.map(s => {
              const sBillId = s.billId || s.id;
              // Update ONLY the specific bill with matching billId
              if (sBillId && updatedBillId && (sBillId.toString() === updatedBillId.toString())) {
                const beforeUpdate = { ...s };
                const updated = updateRowWithPaymentMethod(s, pmColumnKey, amount);
                console.log('Updated specific settled bill (payment method):', {
                  billId: s.billId || s.id,
                  before: beforeUpdate[pmColumnKey],
                  after: updated[pmColumnKey],
                  balanceBefore: beforeUpdate.balance,
                  balanceAfter: updated.balance
                });
                return updated;
              }
              return s;
            });

            // Sort by bill number sequence
            const sortedSettled = sortSupplierRowsBySequence(updatedSettled);

            // Save settled suppliers to localStorage
            if (company?._id) {
              try {
                const settledKey = `rozSettledSuppliers_${company._id}`;
                localStorage.setItem(settledKey, JSON.stringify(sortedSettled));
                console.log('Saved updated settled suppliers to localStorage');
              } catch (error) {
                console.error("Error saving settled suppliers:", error);
              }
            }

            return sortedSettled;
          } else {
            console.log('Settled bill not found for billId:', updatedBillId);
          }
          return prevSettled;
        });

        return updatedRows;
      });

      // Refresh bank balances
      await fetchBankBalances();

      // Clear the amount field (keep supplier selected)
      setPaymentMethodFields(prev => ({
        ...prev,
        [pmFieldKey]: { amount: '' }
      }));

      toast.success(companyLanguage === 'ur'
        ? `ادائیگی لین دین بنایا گیا - ${supplierName}`
        : `Payment transaction created - ${supplierName}`);
    } catch (error) {
      console.error('Error creating payment method payment:', error);
      toast.error(companyLanguage === 'ur'
        ? 'ادائیگی بنانے میں ناکام'
        : 'Failed to create payment');
    }
  };

  // Handle supplier settlement
  const handleSettleSupplier = useCallback((supplierId, supplierName) => {
    console.log('handleSettleSupplier called:', supplierId, supplierName);
    console.log('Current supplierRows:', supplierRows);

    // Find all rows for this supplier
    const allSupplierRows = supplierRows.filter(row => {
      const rowSupplierId = row.supplierName?.toString() || row.supplierName;
      return rowSupplierId === supplierId || rowSupplierId?.toString() === supplierId;
    });

    // Find the specific bill being worked on (same logic as payment updates)
    let supplierRow = null;
    if (allSupplierRows.length > 1) {
      // Find bill with most recent payment activity
      const billsWithPayments = allSupplierRows.filter(row => {
        let hasPayments = false;
        supplierColumns.forEach(col => {
          if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
            const value = parseFloat(row[col.key] || 0);
            if (value > 0.01) {
              hasPayments = true;
            }
          }
        });
        return hasPayments;
      });

      if (billsWithPayments.length > 0) {
        // Use the bill with most payments (highest total)
        supplierRow = billsWithPayments.reduce((max, row) => {
          let maxTotal = 0;
          let rowTotal = 0;
          supplierColumns.forEach(col => {
            if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
              maxTotal += parseFloat(max[col.key] || 0);
              rowTotal += parseFloat(row[col.key] || 0);
            }
          });
          return rowTotal > maxTotal ? row : max;
        });
      } else {
        // No payments yet, use the oldest bill
        supplierRow = allSupplierRows.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }
          const idA = (a.billId || a.id)?.toString() || '';
          const idB = (b.billId || b.id)?.toString() || '';
          return idA.localeCompare(idB);
        })[0];
      }
    } else if (allSupplierRows.length === 1) {
      supplierRow = allSupplierRows[0];
    }

    console.log('Found supplierRow (specific bill):', supplierRow);

    if (supplierRow) {
      const billId = supplierRow.billId || supplierRow.id;

      // Move to settled list
      setSettledSuppliers(prev => {
        console.log('Current settledSuppliers:', prev);
        // Check if this specific bill is already settled (by billId, not just supplierId)
        const alreadySettled = prev.find(s => {
          const sBillId = s.billId || s.id;
          return sBillId && billId && (sBillId.toString() === billId.toString());
        });
        if (alreadySettled) {
          console.log('This bill already settled, returning prev');
          return prev;
        }

        const newSettled = [...prev, { ...supplierRow, settledAt: new Date().toISOString() }];
        console.log('New settledSuppliers:', newSettled);

        // Save to localStorage
        if (company?._id) {
          try {
            const settledKey = `rozSettledSuppliers_${company._id}`;
            localStorage.setItem(settledKey, JSON.stringify(newSettled));
            console.log('Saved settled suppliers to localStorage');
          } catch (error) {
            console.error("Error saving settled suppliers:", error);
          }
        }

        return newSettled;
      });

      // Remove from active list - only remove this specific bill (by billId), not all bills for supplier
      setSupplierRows(prev => {
        const filtered = prev.filter(row => {
          const rowBillId = row.billId || row.id;
          // Only remove if billId matches, not just supplierId
          return !(billId && rowBillId && (rowBillId.toString() === billId.toString()));
        });
        console.log('Filtered supplierRows:', filtered);

        // Save updated supplier rows to localStorage
        if (company?._id) {
          try {
            const supplierKey = `rozSupplierRows_${company._id}`;
            localStorage.setItem(supplierKey, JSON.stringify(filtered));
            console.log('Saved updated supplier rows to localStorage');
          } catch (error) {
            console.error("Error saving supplier rows:", error);
          }
        }

        // Auto-select next bill in sequence after settlement
        if (filtered.length > 0) {
          // Sort by bill number sequence to get next bill
          const sortedRows = sortSupplierRowsBySequence(filtered);
          const nextBill = sortedRows[0]; // Get first bill (next in sequence)

          if (nextBill) {
            const nextBillId = nextBill.id || nextBill.billId;
            const nextSupplierId = nextBill.supplierName?.toString() || nextBill.supplierName;

            // Auto-select next bill
            setTimeout(() => {
              setSelectedBillRowId(nextBillId);
              if (nextSupplierId && /^[0-9a-fA-F]{24}$/.test(nextSupplierId)) {
                setSelectedSupplierId(nextSupplierId);
              }

              // Clear payment fields for next bill
              setBankPaymentFields({});
              setPaymentMethodFields({});
              setExpenseFieldInputs({});

              toast.info(companyLanguage === 'ur'
                ? `اگلا بل خود بخود منتخب ہو گیا`
                : `Next bill auto-selected`);
            }, 500); // Small delay to ensure state updates
          }
        } else {
          // No more bills, clear selection
          setSelectedBillRowId(null);
          setSelectedSupplierId('');
          setBankPaymentFields({});
          setPaymentMethodFields({});
          setExpenseFieldInputs({});
        }

        return filtered;
      });

      toast.success(companyLanguage === 'ur'
        ? `سپلائر settled ہو گیا - ${supplierName}`
        : `Supplier settled - ${supplierName}`);
    } else {
      console.log('Supplier row not found!');
    }
  }, [supplierRows, companyLanguage]);

  // Handle Enter key to move to next field
  const handleEnterKey = useCallback((currentIndex, type, allItems, currentFieldKey) => {
    const nextIndex = currentIndex + 1;

    // If not last item, move to next input field
    if (nextIndex < allItems.length) {
      const nextItem = allItems[nextIndex];
      const nextKey = (nextItem._id?.toString() || nextItem._id);
      const refKey = `${type}_${nextKey}`;

      if (type === 'bank') {
        const ref = bankInputRefs.current[refKey];
        if (ref) {
          setTimeout(() => ref.focus(), 100);
        }
      } else if (type === 'paymentMethod') {
        const ref = paymentMethodInputRefs.current[refKey];
        if (ref) {
          setTimeout(() => ref.focus(), 100);
        }
      }
    } else {
      // Last item - focus and select shared settled checkbox
      if (type === 'bank' && settleButtonRef.current) {
        setTimeout(() => {
          settleButtonRef.current.focus();
        }, 100);
      }
    }
  }, []);

  // Navigate to next input field on Enter
  const moveToNextInput = (currentRefKey) => {
    // Get all input refs in order: banks first, then payment methods, then expense fields
    const allRefs = [];

    // Add bank refs in order
    if (banks && banks.length > 0) {
      banks.forEach((bank) => {
        const bankIdStr = bank._id?.toString();
        const bankFieldKey = bankIdStr || bank._id;
        const refKey = `bank_${bankFieldKey}`;
        const inputRef = bankInputRefs.current[refKey];
        if (inputRef) {
          allRefs.push({ refKey, inputRef });
        }
      });
    }

    // Add payment method refs in order
    if (paymentMethods && paymentMethods.length > 0) {
      paymentMethods.forEach((pm) => {
        const pmIdStr = pm._id?.toString();
        const pmFieldKey = pmIdStr || pm._id;
        const refKey = `paymentMethod_${pmFieldKey}`;
        const inputRef = paymentMethodInputRefs.current[refKey];
        if (inputRef) {
          allRefs.push({ refKey, inputRef });
        }
      });
    }

    // Add expense field refs in order: نقدی, کرایه, کمیشن, مزدوری, مشیانه, مارکیٹ فیس
    const expenseFieldRefKeys = [
      'expense_cash',
      'expense_rent',
      'expense_commission',
      'expense_labor',
      'expense_miscellaneous',
      'expense_market_fees'
    ];
    expenseFieldRefKeys.forEach((refKey) => {
      const inputRef = expenseFieldInputRefs.current[refKey];
      if (inputRef) {
        allRefs.push({ refKey, inputRef });
      }
    });

    // Find current input index
    const currentIndex = allRefs.findIndex(ref => ref.refKey === currentRefKey);

    // Move to next input
    if (currentIndex >= 0 && currentIndex < allRefs.length - 1) {
      const nextRef = allRefs[currentIndex + 1];
      if (nextRef && nextRef.inputRef) {
        setTimeout(() => {
          nextRef.inputRef.focus();
          nextRef.inputRef.select(); // Select text for easy editing
        }, 10);
      }
    }
  };

  // Handle bank payment from top fields
  const handleBankPayment = async (bankFieldKey, bank, fieldData) => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      const amount = parseFloat(fieldData.amount || 0);
      const supplierId = selectedSupplierId; // Use shared supplier

      if (amount <= 0) {
        toast.error(companyLanguage === 'ur' ? 'رقم درست نہیں' : 'Invalid amount');
        return;
      }

      if (!supplierId) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم سپلائر منتخب کریں' : 'Please select supplier');
        return;
      }

      // Find supplier party
      const supplierParty = ledgerParties.find(p => {
        const partyId = p._id?.toString() || p._id;
        return partyId === supplierId || partyId?.toString() === supplierId;
      });

      if (!supplierParty) {
        toast.error(companyLanguage === 'ur' ? 'سپلائر نہیں ملا' : 'Supplier not found');
        return;
      }

      const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);

      // Get bill number from selected bill row or first matching supplier row
      let billNumber = '';
      if (selectedBillRowId) {
        const row = supplierRows.find(r => r.id === selectedBillRowId || r.billId === selectedBillRowId);
        billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';
      } else {
        // Find first matching supplier row
        const row = supplierRows.find(r => {
          const rowSupplierId = r.supplierName?.toString() || r.supplierName;
          return rowSupplierId === supplierId || rowSupplierId?.toString() === supplierId;
        });
        billNumber = row?.billNumber || row?.billNo || row?.serialNumber || '';
      }

      // Create bank transaction (withdraw from bank)
      const bankId = bank._id?.toString() || bank._id;
      const transactionPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'withdraw',
        amount: amount,
        paymentMethod: bank.bankNameUrdu || bank.bankName || '',
        description: companyLanguage === 'ur'
          ? `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
          : `Roznamacha - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - Amount: ${amount.toFixed(2)}`,
        descriptionUrdu: `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${supplierName} - رقم: ${amount.toFixed(2)}`
      };

      console.log('Creating bank transaction:', transactionPayload);
      await api.post(`/bank/company/${bankId}/transaction`, transactionPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create ledger transaction (debit/sale to supplier - goes to sale field)
      const ledgerPayload = {
        date: new Date().toLocaleDateString('en-CA'),
        type: 'debit', // Debit (sale) - goes to sale field in supplier khata
        amount: amount,
        description: companyLanguage === 'ur'
          ? `بینک ادائیگی - ${bank.bankNameUrdu || bank.bankName}`
          : `Bank Payment - ${bank.bankName || bank.bankNameUrdu}`,
        descriptionUrdu: `بینک ادائیگی - ${bank.bankNameUrdu || bank.bankName}`
      };

      console.log('Creating ledger transaction for supplier:', supplierId, ledgerPayload);
      await api.post(`/ledger/parties/${supplierId}/transactions`, ledgerPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Helper function to update a row with bank payment
      const updateRowWithBankPayment = (row, bankColumnKey, amount) => {
        const updatedRow = { ...row };
        updatedRow[bankColumnKey] = (parseFloat(updatedRow[bankColumnKey] || 0) + amount).toFixed(2);

        // Recalculate balance
        const sale = parseFloat(updatedRow.sale || 0);
        const cash = parseFloat(updatedRow.cash || 0);
        const amountField = parseFloat(updatedRow.amount || 0);

        // Sum all bank columns
        let totalBanks = 0;
        supplierColumns.forEach(col => {
          if (col.key && col.key.startsWith('bank_')) {
            totalBanks += parseFloat(updatedRow[col.key] || 0);
          }
        });

        // Sum all payment method columns
        let totalPaymentMethods = 0;
        supplierColumns.forEach(col => {
          if (col.key && col.key.startsWith('paymentMethod_')) {
            totalPaymentMethods += parseFloat(updatedRow[col.key] || 0);
          }
        });

        // Calculate balance: sale minus all payments
        const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amountField;
        updatedRow.balance = calculatedBalance.toFixed(2);

        return updatedRow;
      };

      // Update supplier row in table - find the specific bill being worked on
      // If multiple bills exist for same supplier, work on the one with most recent activity
      setSupplierRows(prevRows => {
        // Find all rows for this supplier
        const supplierRows = prevRows.filter(row => {
          const rowSupplierId = row.supplierName?.toString() || row.supplierName;
          return rowSupplierId === supplierId || rowSupplierId?.toString() === supplierId;
        });

        // If multiple bills, find the one with most recent payment activity
        let supplierRow = null;
        if (supplierRows.length > 1) {
          // Find bill with most recent payment activity (has bank/payment method values)
          const billsWithPayments = supplierRows.filter(row => {
            let hasPayments = false;
            supplierColumns.forEach(col => {
              if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
                const value = parseFloat(row[col.key] || 0);
                if (value > 0.01) {
                  hasPayments = true;
                }
              }
            });
            return hasPayments;
          });

          if (billsWithPayments.length > 0) {
            // Use the bill with most payments (highest total)
            supplierRow = billsWithPayments.reduce((max, row) => {
              let maxTotal = 0;
              let rowTotal = 0;
              supplierColumns.forEach(col => {
                if (col.key && (col.key.startsWith('bank_') || col.key.startsWith('paymentMethod_'))) {
                  maxTotal += parseFloat(max[col.key] || 0);
                  rowTotal += parseFloat(row[col.key] || 0);
                }
              });
              return rowTotal > maxTotal ? row : max;
            });
          } else {
            // No payments yet, use the oldest bill (earliest date or lowest billId)
            supplierRow = supplierRows.sort((a, b) => {
              const dateA = new Date(a.date || 0);
              const dateB = new Date(b.date || 0);
              if (dateA.getTime() !== dateB.getTime()) {
                return dateA - dateB;
              }
              // If same date, use billId
              const idA = (a.billId || a.id)?.toString() || '';
              const idB = (b.billId || b.id)?.toString() || '';
              return idA.localeCompare(idB);
            })[0];
          }
        } else if (supplierRows.length === 1) {
          supplierRow = supplierRows[0];
        }

        if (!supplierRow) {
          // Create new row for this supplier
          supplierRow = {
            id: Date.now() + Math.random(),
            date: new Date().toLocaleDateString('en-CA'),
            supplierName: supplierId,
            sale: '',
            cash: '',
            balance: ''
          };

          // Add dynamic columns
          supplierColumns.forEach(col => {
            if (!col.isDefault && col.key) {
              supplierRow[col.key] = '';
            }
          });

          prevRows = [...prevRows, supplierRow];
        }

        // Get the billId of the bill that was updated (before updating)
        const updatedBillId = supplierRow.billId || supplierRow.id;

        // Update bank column in the row
        const bankColumnKey = `bank_${bankId}`;
        let updatedBillRow = null;
        const updatedRows = prevRows.map(row => {
          if (row.id === supplierRow.id) {
            updatedBillRow = updateRowWithBankPayment(row, bankColumnKey, amount);
            return updatedBillRow;
          }
          return row;
        });

        // Check if bill should be auto-settled (balance <= 0.01)
        if (updatedBillRow) {
          const balance = parseFloat(updatedBillRow.balance || 0);
          if (Math.abs(balance) < 0.01) {
            // Bill is fully paid, automatically move to settled list
            const billId = updatedBillRow.billId || updatedBillRow.id;
            const supplierName = updatedBillRow.supplierName || supplierId;

            // Move to settled list
            setSettledSuppliers(prev => {
              const alreadySettled = prev.find(s => {
                const sBillId = s.billId || s.id;
                return sBillId && billId && (sBillId.toString() === billId.toString());
              });
              if (alreadySettled) {
                return prev;
              }

              const newSettled = [...prev, { ...updatedBillRow, settledAt: new Date().toISOString() }];

              // Save to localStorage
              if (company?._id) {
                try {
                  const settledKey = `rozSettledSuppliers_${company._id}`;
                  localStorage.setItem(settledKey, JSON.stringify(newSettled));
                } catch (error) {
                  console.error("Error saving settled suppliers:", error);
                }
              }

              return newSettled;
            });

            // Remove from active list
            const filteredRows = updatedRows.filter(row => {
              const rowBillId = row.billId || row.id;
              return !(billId && rowBillId && (rowBillId.toString() === billId.toString()));
            });

            // Save filtered rows to localStorage
            if (company?._id) {
              try {
                const supplierKey = `rozSupplierRows_${company._id}`;
                localStorage.setItem(supplierKey, JSON.stringify(filteredRows));
              } catch (error) {
                console.error("Error saving supplier rows:", error);
              }
            }

            toast.success(companyLanguage === 'ur'
              ? `بل خود بخود settled ہو گیا - ${supplierName}`
              : `Bill automatically settled - ${supplierName}`);

            return filteredRows;
          }
        }

        // Save to localStorage
        if (company?._id) {
          try {
            const supplierKey = `rozSupplierRows_${company._id}`;
            localStorage.setItem(supplierKey, JSON.stringify(updatedRows));
          } catch (error) {
            console.error("Error saving supplier rows:", error);
          }
        }

        // Also update settled suppliers - update ONLY the specific bill that was updated (by billId)
        setSettledSuppliers(prevSettled => {
          // Find the specific bill in settled list by billId (not just supplierId)
          const settledBill = prevSettled.find(s => {
            const sBillId = s.billId || s.id;
            return sBillId && updatedBillId && (sBillId.toString() === updatedBillId.toString());
          });

          if (settledBill) {
            console.log('Updating specific settled bill by billId:', updatedBillId, 'bank column:', bankColumnKey, 'with amount:', amount);

            const updatedSettled = prevSettled.map(s => {
              const sBillId = s.billId || s.id;
              // Update ONLY the specific bill with matching billId
              if (sBillId && updatedBillId && (sBillId.toString() === updatedBillId.toString())) {
                const beforeUpdate = { ...s };
                const updated = updateRowWithBankPayment(s, bankColumnKey, amount);
                console.log('Updated specific settled bill:', {
                  billId: s.billId || s.id,
                  before: beforeUpdate[bankColumnKey],
                  after: updated[bankColumnKey],
                  balanceBefore: beforeUpdate.balance,
                  balanceAfter: updated.balance
                });
                return updated;
              }
              return s;
            });

            // Sort by bill number sequence
            const sortedSettled = sortSupplierRowsBySequence(updatedSettled);

            // Save settled suppliers to localStorage
            if (company?._id) {
              try {
                const settledKey = `rozSettledSuppliers_${company._id}`;
                localStorage.setItem(settledKey, JSON.stringify(sortedSettled));
                console.log('Saved updated settled suppliers to localStorage');
              } catch (error) {
                console.error("Error saving settled suppliers:", error);
              }
            }

            return sortedSettled;
          } else {
            console.log('Settled bill not found for billId:', updatedBillId);
          }
          return prevSettled;
        });

        return updatedRows;
      });

      // Refresh bank balances
      await fetchBankBalances();

      // Clear the amount field (keep supplier selected)
      setBankPaymentFields(prev => ({
        ...prev,
        [bankFieldKey]: { amount: '' }
      }));

      toast.success(companyLanguage === 'ur'
        ? `بینک لین دین بنایا گیا - ${supplierName}`
        : `Bank transaction created - ${supplierName}`);
    } catch (error) {
      console.error('Error creating bank payment:', error);
      toast.error(companyLanguage === 'ur'
        ? 'بینک ادائیگی بنانے میں ناکام'
        : 'Failed to create bank payment');
    }
  };

  const handleSave = async (type, silent = false) => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem("companyToken");
      if (!token) {
        if (!silent) {
          toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        }
        return;
      }

      const rows = type === 'supplier' ? supplierRows : customerRows;
      const columns = type === 'supplier' ? supplierColumns : customerColumns;
      let transactionsCreated = 0;
      let errors = [];

      // Track which rows and columns had transactions created (to update saved values only for those)
      const updatedSavedValues = {}; // { rowId: { colKey: newSavedValue } }

      // Process each row and create bank transactions for bank columns with values
      // Only create transaction for the difference (new value - saved value)
      for (const row of rows) {
        const rowId = row.id;
        if (!updatedSavedValues[rowId]) {
          updatedSavedValues[rowId] = {};
        }

        // Check all bank columns
        for (const col of columns) {
          if (col.key && col.key.startsWith('bank_')) {
            const currentValue = parseFloat(row[col.key] || 0);
            const savedValue = parseFloat(row[`_saved_${col.key}`] || 0); // Get saved value
            const difference = currentValue - savedValue; // Calculate difference

            // Only create transaction if there's a meaningful difference
            if (Math.abs(difference) > 0.01) {
              try {
                const bankId = col.bankId || col.key.replace('bank_', '');
                const bank = banks.find(b => {
                  const bId = b._id?.toString() || b._id;
                  const colBankId = bankId?.toString() || bankId;
                  return bId === colBankId || b._id.toString() === colBankId;
                });

                if (bank) {
                  // Use bank's _id to post transaction to bank's ledger
                  const bankId = bank._id?.toString() || bank._id;

                  // Get supplier/customer name for description
                  let partyNameForDescription = row.supplierName || row.customerName || '';

                  // If it's a party ID, get the actual name from ledgerParties
                  if (partyNameForDescription) {
                    const party = ledgerParties.find(p => {
                      const partyId = p._id?.toString() || p._id;
                      const nameId = partyNameForDescription?.toString() || partyNameForDescription;
                      return partyId === nameId;
                    });

                    if (party) {
                      partyNameForDescription = party.nameUrdu || party.name || partyNameForDescription;
                    } else {
                      partyNameForDescription = row.customerNameUrdu || row.customerName || row.supplierName || '';
                    }
                  }

                  const transactionType = difference > 0 ? 'withdraw' : 'deposit';
                  const transactionAmount = Math.abs(difference);

                  // Get bill number from row
                  const billNumber = row.billNumber || row.billNo || row.serialNumber || '';

                  const payload = {
                    date: row.date || new Date().toLocaleDateString('en-CA'),
                    type: transactionType,
                    amount: transactionAmount,
                    paymentMethod: bank.bankNameUrdu || bank.bankName || '',
                    description: companyLanguage === 'ur'
                      ? `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${partyNameForDescription} - رقم: ${transactionAmount.toFixed(2)}`
                      : `Roznamacha - ${billNumber ? billNumber + ' - ' : ''}${partyNameForDescription} - Amount: ${transactionAmount.toFixed(2)}`,
                    descriptionUrdu: `روزنامچہ - ${billNumber ? billNumber + ' - ' : ''}${partyNameForDescription} - رقم: ${transactionAmount.toFixed(2)}`
                  };

                  console.log(`Creating bank transaction on auto-save (posted to bank ledger): ${transactionType} ${transactionAmount} for bank ${bank.bankName} (${bankId})`, payload);
                  // Post transaction to bank's specific ledger
                  await api.post(`/bank/company/${bankId}/transaction`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                  });

                  // Track that this row/column had a transaction created - update saved value after all transactions
                  updatedSavedValues[rowId][col.key] = currentValue;

                  transactionsCreated++;
                  console.log(`✅ Bank transaction created: ${transactionType} ${transactionAmount} for bank ${bank.bankName}`);
                }
              } catch (error) {
                console.error(`Error creating transaction for bank ${col.key}:`, error);
                errors.push(`${col.key}: ${error.message || 'Failed'}`);
              }
            }
          }

          // Check all payment method columns
          if (col.key && col.key.startsWith('paymentMethod_')) {
            const paymentMethodValue = parseFloat(row[col.key] || 0);

            // If there's a value in payment method column, create withdrawal transaction
            if (paymentMethodValue > 0.01) {
              try {
                const paymentMethodId = col.paymentMethodId || col.key.replace('paymentMethod_', '');
                const paymentMethod = paymentMethods.find(pm => {
                  const pmId = pm._id?.toString() || pm._id;
                  const colPmId = paymentMethodId?.toString() || paymentMethodId;
                  return pmId === colPmId || pm._id.toString() === colPmId;
                });

                if (paymentMethod) {
                  const payload = {
                    paymentMethodId: paymentMethod._id,
                    date: row.date || new Date().toLocaleDateString('en-CA'),
                    type: 'withdraw',
                    amount: paymentMethodValue,
                    description: companyLanguage === 'ur'
                      ? `روزنامچہ - ${row.supplierName || row.customerName || ''}`
                      : `Roznamacha - ${row.supplierName || row.customerName || ''}`,
                    descriptionUrdu: `روزنامچہ - ${row.supplierName || row.customerName || ''}`
                  };

                  console.log('Creating payment method transaction on auto-save:', payload);
                  await api.post(`/bank/company/payment-method/transaction`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                  });

                  transactionsCreated++;
                  console.log(`✅ Payment method transaction created: withdraw ${paymentMethodValue} for ${paymentMethod.name}`);
                }
              } catch (error) {
                console.error(`Error creating transaction for payment method ${col.key}:`, error);
                errors.push(`${col.key}: ${error.message || 'Failed'}`);
              }
            }
          }
        }
      }

      // Refresh bank balances after creating transactions
      if (transactionsCreated > 0) {
        setTimeout(async () => {
          await fetchBankBalances();
        }, 500);
      }

      // Show success/error messages only if not silent
      if (!silent) {
        if (transactionsCreated > 0) {
          toast.success(companyLanguage === 'ur'
            ? `${transactionsCreated} بینک لین دین بنائے گئے`
            : `${transactionsCreated} bank transactions created`);
        }

        if (errors.length > 0) {
          toast.error(companyLanguage === 'ur'
            ? `${errors.length} لین دین بنانے میں ناکام`
            : `Failed to create ${errors.length} transactions`);
        }
      }

      // Update saved values in state ONLY for rows/columns that had transactions created
      if (type === 'supplier') {
        setSupplierRows(prevRows => {
          const updatedRows = prevRows.map(row => {
            const updatedRow = { ...row };
            // Only update saved values for columns that had transactions created
            if (updatedSavedValues[row.id]) {
              Object.keys(updatedSavedValues[row.id]).forEach(colKey => {
                updatedRow[`_saved_${colKey}`] = updatedSavedValues[row.id][colKey];
              });
            }
            return updatedRow;
          });

          // Save to localStorage to persist _saved_ values
          setTimeout(() => {
            if (company?._id) {
              try {
                const supplierKey = `rozSupplierRows_${company._id}`;
                localStorage.setItem(supplierKey, JSON.stringify(updatedRows));
              } catch (error) {
                console.error("Error saving supplier rows with _saved_ values:", error);
              }
            }
          }, 0);

          return updatedRows;
        });
        // TODO: Implement backend API to save supplier roz namacha
        console.log('Auto-saving supplier roz namacha:', supplierRows);
        if (!silent) {
          toast.success(companyLanguage === 'ur' ? 'سپلائر روزنامچہ محفوظ ہو گیا' : 'Supplier Roz Namacha saved');
        }
      } else {
        setCustomerRows(prevRows => {
          const updatedRows = prevRows.map(row => {
            const updatedRow = { ...row };
            // Only update saved values for columns that had transactions created
            if (updatedSavedValues[row.id]) {
              Object.keys(updatedSavedValues[row.id]).forEach(colKey => {
                updatedRow[`_saved_${colKey}`] = updatedSavedValues[row.id][colKey];
              });
            }
            return updatedRow;
          });

          // Save to localStorage to persist _saved_ values
          setTimeout(() => {
            if (company?._id) {
              try {
                const customerKey = `rozCustomerRows_${company._id}`;
                localStorage.setItem(customerKey, JSON.stringify(updatedRows));
              } catch (error) {
                console.error("Error saving customer rows with _saved_ values:", error);
              }
            }
          }, 0);

          return updatedRows;
        });
        // TODO: Implement backend API to save customer roz namacha
        console.log('Auto-saving customer roz namacha:', customerRows);
        if (!silent) {
          toast.success(companyLanguage === 'ur' ? 'گاہک روزنامچہ محفوظ ہو گیا' : 'Customer Roz Namacha saved');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      if (!silent) {
        toast.error(companyLanguage === 'ur' ? 'محفوظ کرنے میں ناکام' : 'Failed to save');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (id, type) => {
    setEditingRowId(id);
  };

  // Calculate settlement for a bill
  const calculateBillSettlement = (row) => {
    if (!row.billId) return null;

    const sale = parseFloat(row.sale || 0);
    const cash = parseFloat(row.cash || 0);
    const amount = parseFloat(row.amount || 0);

    // Calculate payments from bank columns
    const bankPayments = [];
    let totalBankPayments = 0;
    supplierColumns.forEach(col => {
      if (col.key && col.key.startsWith('bank_')) {
        const bankValue = parseFloat(row[col.key] || 0);
        if (bankValue > 0.01) {
          const bankId = col.bankId || col.key.replace('bank_', '');
          const bank = banks.find(b => {
            const bId = b._id?.toString() || b._id;
            const colBankId = bankId?.toString() || bankId;
            return bId === colBankId || b._id.toString() === colBankId;
          });
          if (bank) {
            bankPayments.push({
              bankName: bank.bankNameUrdu || bank.bankName,
              amount: bankValue
            });
            totalBankPayments += bankValue;
          }
        }
      }
    });

    // Calculate payments from payment method columns
    const paymentMethodPayments = [];
    let totalPaymentMethodPayments = 0;
    supplierColumns.forEach(col => {
      if (col.key && col.key.startsWith('paymentMethod_')) {
        const pmValue = parseFloat(row[col.key] || 0);
        if (pmValue > 0.01) {
          const paymentMethodId = col.paymentMethodId || col.key.replace('paymentMethod_', '');
          const paymentMethod = paymentMethods.find(pm => {
            const pmId = pm._id?.toString() || pm._id;
            const colPmId = paymentMethodId?.toString() || paymentMethodId;
            return pmId === colPmId || pm._id.toString() === colPmId;
          });
          if (paymentMethod) {
            paymentMethodPayments.push({
              methodName: paymentMethod.nameUrdu || paymentMethod.name,
              amount: pmValue
            });
            totalPaymentMethodPayments += pmValue;
          }
        }
      }
    });

    const totalPayments = cash + totalBankPayments + totalPaymentMethodPayments + amount;
    const balance = sale - totalPayments;

    return {
      billId: row.billId,
      sale,
      cash,
      amount,
      bankPayments,
      paymentMethodPayments,
      totalBankPayments,
      totalPaymentMethodPayments,
      totalPayments,
      balance,
      isFullyPaid: Math.abs(balance) < 0.01,
      paymentPercentage: sale > 0 ? ((totalPayments / sale) * 100).toFixed(1) : 0
    };
  };

  const handleViewSettlement = (row) => {
    const settlement = calculateBillSettlement(row);
    if (settlement) {
      setSelectedBillSettlement(settlement);
      setShowSettlementModal(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>لوڈ ہو رہا ہے...</p>
        </div>
      </div>
    );
  }

  const isUrdu = companyLanguage === 'ur';

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'بل ادائیگی' : 'Settled Bill'}</title>
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fadeInUp 0.5s ease-out forwards;
          }
        `}</style>
      </Helmet>

      {/* Company Header - Hidden on SettledBill page */}
      {/* {company && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <div className={`min-h-screen bg-gray-100 pt-[6px] pb-8 px-4 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-white to-green-50/30 rounded-xl shadow-lg border border-green-100 mb-4" style={{ overflow: 'visible' }}>
            <div className="flex justify-between items-center py-6 px-5" style={{ minHeight: '80px', overflow: 'visible' }}>
              <h1 className={`text-2xl font-bold text-green-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? {
                fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                lineHeight: '2',
                display: 'block',
                padding: '0.5rem 0',
                margin: 0,
                overflow: 'visible',
                height: 'auto',
                whiteSpace: 'nowrap'
              } : {
                lineHeight: '2',
                display: 'block',
                padding: '0.5rem 0',
                margin: 0,
                overflow: 'visible',
                height: 'auto',
                whiteSpace: 'nowrap'
              }}>
                {isUrdu ? 'بل ادائیگی' : 'Settled Bill'}
              </h1>
              <button
                onClick={() => navigate('/company/dashboard')}
                className={`group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2 overflow-hidden ${isUrdu ? 'font-urdu' : ''}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                <svg className="w-5 h-5 relative z-10 transform group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="relative z-10">{isUrdu ? 'ڈیش بورڈ' : 'Dashboard'}</span>
              </button>
            </div>
          </div>

          {/* Bank Fields Section - Simple Disabled Fields */}
          {company && (() => {
            // Get banks from supplierColumns (already synced)
            const bankColumns = supplierColumns.filter(col => col.key && col.key.startsWith('bank_'));
            const paymentMethodColumns = supplierColumns.filter(col => col.key && col.key.startsWith('paymentMethod_'));

            // Use banks array if available, otherwise use columns
            const banksToShow = (banks && Array.isArray(banks) && banks.length > 0) ? banks : bankColumns.map(col => ({
              _id: col.bankId || col.key.replace('bank_', ''),
              bankName: col.labelEn,
              bankNameUrdu: col.labelUr
            }));

            const paymentMethodsToShow = (paymentMethods && Array.isArray(paymentMethods) && paymentMethods.length > 0)
              ? paymentMethods.filter(pm => pm.isActive !== false)
              : paymentMethodColumns.map(col => ({
                _id: col.paymentMethodId || col.key.replace('paymentMethod_', ''),
                name: col.labelEn,
                nameUrdu: col.labelUr
              }));

            const hasBanks = banksToShow.length > 0;
            const hasPaymentMethods = paymentMethodsToShow.length > 0;

            if (!hasBanks && !hasPaymentMethods) return null;

            return (
              <div className="mb-4 bg-white rounded-xl shadow-lg border border-green-100 p-3">
                {/* Shared Supplier Field - Modern Compact */}
                <div className="mb-3">
                  <div
                    className="relative overflow-hidden rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 shadow-sm transition-all duration-300 hover:shadow-md"
                  >
                    <div className="p-3">
                      <label className={`block text-xs font-semibold mb-1.5 text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'سپلائر منتخب کریں' : 'Select Supplier'}
                      </label>
                      <select
                        value={selectedSupplierId || ''}
                        onChange={(e) => setSelectedSupplierId(e.target.value)}
                        className={`w-full px-3 py-2 border border-blue-300 rounded-lg text-sm font-medium bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isUrdu ? 'font-urdu' : ''}`}
                        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                      >
                        <option value="">{isUrdu ? 'سپلائر منتخب کریں' : 'Select Supplier'}</option>
                        {ledgerParties.filter(p => p.type === 'supplier').map(party => (
                          <option key={party._id} value={party._id}>
                            {isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu)}
                            {party.phone ? ` - ${party.phone}` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedSupplierId && (() => {
                        const selectedParty = ledgerParties.find(p => {
                          const partyId = p._id?.toString() || p._id;
                          return partyId === selectedSupplierId || partyId?.toString() === selectedSupplierId;
                        });

                        // Find the specific supplier bill row to get balance
                        // If a specific bill row is selected, use that; otherwise find first matching supplier
                        const supplierRow = selectedBillRowId
                          ? supplierRows.find(row => row.id === selectedBillRowId || row.billId === selectedBillRowId)
                          : supplierRows.find(row => {
                            const rowSupplierId = row.supplierName?.toString() || row.supplierName;
                            return rowSupplierId === selectedSupplierId || rowSupplierId?.toString() === selectedSupplierId;
                          });

                        const originalBalance = supplierRow?.balance ? parseFloat(supplierRow.balance) : null;

                        // Calculate total entered amounts from all bank fields
                        let totalBankAmount = 0;
                        for (const [bankFieldKey, fieldData] of Object.entries(bankPaymentFields)) {
                          if (fieldData.amount && parseFloat(fieldData.amount) > 0) {
                            totalBankAmount += parseFloat(fieldData.amount);
                          }
                        }

                        // Calculate total entered amounts from all payment method fields
                        let totalPaymentMethodAmount = 0;
                        for (const [pmFieldKey, fieldData] of Object.entries(paymentMethodFields)) {
                          if (fieldData.amount && parseFloat(fieldData.amount) > 0) {
                            totalPaymentMethodAmount += parseFloat(fieldData.amount);
                          }
                        }

                        // Calculate live balance by subtracting entered amounts
                        const totalEnteredAmount = totalBankAmount + totalPaymentMethodAmount;
                        const liveBalance = originalBalance !== null ? originalBalance - totalEnteredAmount : null;

                        return selectedParty ? (
                          <div className="mt-2 px-2.5 py-1.5 bg-blue-50 rounded-md border border-blue-200">
                            <p className={`text-xs font-medium text-blue-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'منتخب شدہ: ' : 'Selected: '}
                              <span className="font-semibold">{isUrdu ? (selectedParty.nameUrdu || selectedParty.name) : (selectedParty.name || selectedParty.nameUrdu)}</span>
                              {selectedParty.phone && <span className="text-blue-600"> - {selectedParty.phone}</span>}
                              {liveBalance !== null && (
                                <span className={`${isUrdu ? 'mr-2' : 'ml-2'} font-bold`} style={{ color: liveBalance >= 0 ? '#059669' : '#dc2626' }}>
                                  {isUrdu ? `(باقی: ${liveBalance.toFixed(2)})` : `(Bal: ${liveBalance.toFixed(2)})`}
                                </span>
                              )}
                            </p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Bill Details and Payment Methods Layout */}
                {selectedSupplierId && (() => {
                  const selectedParty = ledgerParties.find(p => {
                    const partyId = p._id?.toString() || p._id;
                    return partyId === selectedSupplierId || partyId?.toString() === selectedSupplierId;
                  });

                  // Find the specific supplier bill row
                  const supplierRow = selectedBillRowId
                    ? supplierRows.find(row => row.id === selectedBillRowId || row.billId === selectedBillRowId)
                    : supplierRows.find(row => {
                      const rowSupplierId = row.supplierName?.toString() || row.supplierName;
                      return rowSupplierId === selectedSupplierId || rowSupplierId?.toString() === selectedSupplierId;
                    });

                  if (!supplierRow || !selectedParty) return null;

                  // Get the full bill data to access expenseFields
                  const billId = supplierRow.billId || supplierRow.id;
                  const fullBill = bills.find(b => b._id?.toString() === billId?.toString());
                  const expenseFields = fullBill?.expenseFields || [];
                  const billExpenses = fullBill?.expenses || {};

                  // Calculate all payments
                  let totalBankPayments = 0;
                  let totalPaymentMethodPayments = 0;
                  const bankPaymentsList = [];
                  const paymentMethodPaymentsList = [];

                  supplierColumns.forEach(col => {
                    if (col.key && col.key.startsWith('bank_')) {
                      const amount = parseFloat(supplierRow[col.key] || 0);
                      if (amount > 0.01) {
                        totalBankPayments += amount;
                        const bankId = col.key.replace('bank_', '');
                        const bank = banks.find(b => b._id?.toString() === bankId);
                        bankPaymentsList.push({
                          name: isUrdu ? (bank?.bankNameUrdu || bank?.bankName) : (bank?.bankName || bank?.bankNameUrdu),
                          amount: amount
                        });
                      }
                    }
                    if (col.key && col.key.startsWith('paymentMethod_')) {
                      const amount = parseFloat(supplierRow[col.key] || 0);
                      if (amount > 0.01) {
                        totalPaymentMethodPayments += amount;
                        const pmId = col.key.replace('paymentMethod_', '');
                        const pm = paymentMethods.find(p => p._id?.toString() === pmId);
                        paymentMethodPaymentsList.push({
                          name: isUrdu ? (pm?.nameUrdu || pm?.name) : (pm?.name || pm?.nameUrdu),
                          amount: amount
                        });
                      }
                    }
                  });

                  const sale = parseFloat(supplierRow.sale || 0);
                  const cash = parseFloat(supplierRow.cash || 0);
                  const amount = parseFloat(supplierRow.amount || 0);
                  const balance = parseFloat(supplierRow.balance || 0);
                  const totalPayments = cash + amount + totalBankPayments + totalPaymentMethodPayments;

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      {/* Payment Methods Section - Left Column */}
                      <div className="w-full">
                        <div className="bg-white rounded-lg border border-green-200 shadow-md overflow-hidden">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-3 py-2 shadow-sm">
                            <h3 className={`text-sm font-bold text-white ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'ادائیگی کے طریقے' : 'Payment Methods'}
                            </h3>
                          </div>
                          {/* Grid Layout: 1 column */}
                          <div className="p-2.5 flex flex-col gap-2">
                            {/* Banks */}
                            {hasBanks && banksToShow.map((bank, index) => {
                              // Get balance for this bank
                              const bankIdStr = bank._id?.toString();
                              const balance = bankBalances[bankIdStr] ||
                                bankBalances[bank._id] ||
                                bankBalances[bankIdStr?.toString()] ||
                                bankBalances[bank.bankName] ||
                                bankBalances[bank.bankNameUrdu] ||
                                0;

                              const bankFieldKey = bankIdStr || bank._id;
                              const fieldData = bankPaymentFields[bankFieldKey] || { amount: '' };
                              const refKey = `bank_${bankFieldKey}`;

                              // Calculate live balance by subtracting entered amount
                              const enteredAmount = parseFloat(fieldData.amount || 0);
                              const originalBalance = parseFloat(balance || 0);
                              const liveBalance = originalBalance - enteredAmount;

                              return (
                                <div
                                  key={bank._id || index}
                                  className="flex flex-col border border-green-200 rounded-lg p-2.5 bg-gradient-to-br from-green-50/50 to-emerald-50/50 shadow-sm hover:shadow-md transition-all duration-200"
                                  dir={isUrdu ? 'rtl' : 'ltr'}
                                >
                                  <label className={`text-xs font-semibold mb-1.5 text-green-700 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                    {isUrdu ? (bank.bankNameUrdu || bank.bankName) : (bank.bankName || bank.bankNameUrdu)}
                                    <span className={`${isUrdu ? 'mr-1.5' : 'ml-1.5'} text-green-600 font-medium text-xs`}>
                                      ({liveBalance.toFixed(2)})
                                    </span>
                                  </label>

                                  {/* Amount Input */}
                                  <div className="flex gap-2">
                                    <input
                                      ref={(el) => { bankInputRefs.current[refKey] = el; }}
                                      type="number"
                                      value={fieldData.amount || ''}
                                      onChange={(e) => {
                                        setBankPaymentFields(prev => ({
                                          ...prev,
                                          [bankFieldKey]: {
                                            ...prev[bankFieldKey],
                                            amount: e.target.value
                                          }
                                        }));
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const currentValue = e.target.value;
                                          if (currentValue) {
                                            setBankPaymentFields(prev => ({
                                              ...prev,
                                              [bankFieldKey]: {
                                                ...prev[bankFieldKey],
                                                amount: currentValue
                                              }
                                            }));
                                          }
                                          // Move to next input field
                                          moveToNextInput(refKey);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const currentValue = e.target.value;
                                        if (currentValue) {
                                          setBankPaymentFields(prev => ({
                                            ...prev,
                                            [bankFieldKey]: {
                                              ...prev[bankFieldKey],
                                              amount: currentValue
                                            }
                                          }));
                                        }
                                      }}
                                      className={`flex-1 px-2.5 py-1.5 border border-green-300 rounded-md text-sm bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}
                                      style={{ textAlign: isUrdu ? 'right' : 'left' }}
                                      placeholder={isUrdu ? 'رقم' : 'Amount'}
                                      step="0.01"
                                    />
                                  </div>
                                </div>
                              );
                            })}

                            {/* Payment Method Fields */}
                            {hasPaymentMethods && paymentMethodsToShow.map((pm, index) => {
                              // Adjust index to account for banks
                              const adjustedIndex = (hasBanks ? banksToShow.length : 0) + index;
                              // Get balance for this payment method
                              const pmIdStr = pm._id?.toString();
                              const balance = bankBalances[pmIdStr] ||
                                bankBalances[pm._id] ||
                                bankBalances[pmIdStr?.toString()] ||
                                bankBalances[`paymentMethod_${pmIdStr}`] ||
                                bankBalances[`paymentMethod_${pm._id}`] ||
                                bankBalances[pm.name] ||
                                bankBalances[pm.nameUrdu] ||
                                0;

                              const pmFieldKey = pmIdStr || pm._id;
                              const fieldData = paymentMethodFields[pmFieldKey] || { amount: '' };
                              const refKey = `paymentMethod_${pmFieldKey}`;

                              // Calculate live balance by subtracting entered amount
                              const enteredAmount = parseFloat(fieldData.amount || 0);
                              const originalBalance = parseFloat(balance || 0);
                              const liveBalance = originalBalance - enteredAmount;

                              return (
                                <div
                                  key={pm._id || index}
                                  className="flex flex-col border border-green-200 rounded-lg p-2.5 bg-gradient-to-br from-green-50/50 to-emerald-50/50 shadow-sm hover:shadow-md transition-all duration-200"
                                  dir={isUrdu ? 'rtl' : 'ltr'}
                                >
                                  <label className={`text-xs font-semibold mb-1.5 text-green-700 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                    {isUrdu ? (pm.nameUrdu || pm.name) : (pm.name || pm.nameUrdu)}
                                    <span className={`${isUrdu ? 'mr-1.5' : 'ml-1.5'} text-green-600 font-medium text-xs`}>
                                      ({liveBalance.toFixed(2)})
                                    </span>
                                  </label>

                                  {/* Amount Input */}
                                  <div className="flex gap-2">
                                    <input
                                      ref={(el) => { paymentMethodInputRefs.current[refKey] = el; }}
                                      type="number"
                                      value={fieldData.amount || ''}
                                      onChange={(e) => {
                                        setPaymentMethodFields(prev => ({
                                          ...prev,
                                          [pmFieldKey]: {
                                            ...prev[pmFieldKey],
                                            amount: e.target.value
                                          }
                                        }));
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const currentValue = e.target.value;
                                          if (currentValue) {
                                            setPaymentMethodFields(prev => ({
                                              ...prev,
                                              [pmFieldKey]: {
                                                ...prev[pmFieldKey],
                                                amount: currentValue
                                              }
                                            }));
                                          }
                                          // Move to next input field
                                          moveToNextInput(refKey);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const currentValue = e.target.value;
                                        if (currentValue) {
                                          setPaymentMethodFields(prev => ({
                                            ...prev,
                                            [pmFieldKey]: {
                                              ...prev[pmFieldKey],
                                              amount: currentValue
                                            }
                                          }));
                                        }
                                      }}
                                      className={`flex-1 px-2.5 py-1.5 border border-green-300 rounded-md text-sm bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}
                                      style={{ textAlign: isUrdu ? 'right' : 'left' }}
                                      placeholder={isUrdu ? 'رقم' : 'Amount'}
                                      step="0.01"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Bill Details Card - Second */}
                      <div className="w-full">
                        <div className="bg-white rounded-lg border border-purple-200 shadow-md overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 px-3 py-2 shadow-sm">
                            <h3 className={`text-sm font-bold text-white ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'بل کی تفصیلات' : 'Bill Details'}
                            </h3>
                          </div>

                          <div className="p-2.5 space-y-2">
                            {/* Simple Two-Column List - Expense Fields (Only specific fields from image) */}
                            {(() => {
                              // Define allowed fields in order from image: نقدی, کرایه, کمیشن, مزدوری, مشیانه, مارکیٹ فیس
                              // Include multiple variations for each field
                              const orderedFields = [
                                { en: ['Cash', 'نقدی'], ur: 'نقدی' },
                                { en: ['Rent', 'کرایه', 'Kiraya'], ur: 'کرایه' },
                                { en: ['Commission', 'کمیشن'], ur: 'کمیشن' },
                                { en: ['Labor', 'Wages', 'مزدوری', 'Mazdoori'], ur: 'مزدوری' },
                                { en: ['Miscellaneous', 'Mashiyana', 'Brokerage', 'Agent Fee', 'مشیانه', 'مشیانہ', 'Mashiana', 'Mishiana', 'Mishiyana'], ur: 'مشیانه' },
                                { en: ['Market Fees', 'Market Fee', 'مارکیٹ فیس', 'MarketFees', 'Market', 'Fees'], ur: 'مارکیٹ فیس' }
                              ];

                              // Build the list in order
                              const displayFields = [];

                              // Debug: Log available data for troubleshooting
                              if (expenseFields.length > 0) {
                                console.log('ExpenseFields available:', expenseFields.map(f => ({ en: f.fieldName, ur: f.fieldNameUrdu })));
                              }
                              if (Object.keys(billExpenses).length > 0) {
                                console.log('BillExpenses keys:', Object.keys(billExpenses));
                                console.log('BillExpenses values:', billExpenses);
                              }

                              orderedFields.forEach(orderedField => {
                                let fieldValue = 0;
                                let displayName = isUrdu ? orderedField.ur : orderedField.en[0];

                                // Special handling for Mashiana/Miscellaneous
                                if (orderedField.ur === 'مشیانه') {
                                  // Try common keys for Miscellaneous/Mashiyana first
                                  const mashianaKeys = ['Miscellaneous', 'Mashiyana', 'مشیانہ', 'مشیانه', 'Mashiana', 'Mishiana'];
                                  for (const key of mashianaKeys) {
                                    if (billExpenses[key] !== undefined && billExpenses[key] !== null) {
                                      const val = parseFloat(billExpenses[key] || 0);
                                      if (val >= 0) { // Include 0 values
                                        fieldValue = val;
                                        displayName = isUrdu ? orderedField.ur : key;
                                        console.log(`Found Mashiana from key "${key}": ${fieldValue}`);
                                        break;
                                      }
                                    }
                                  }
                                }

                                // Try to find value from expenseFields first
                                if (expenseFields.length > 0 && fieldValue === 0) {
                                  const matchedField = expenseFields.find(field => {
                                    const fieldNameEn = (field.fieldName || '').toLowerCase().trim();
                                    const fieldNameUr = (field.fieldNameUrdu || '').trim();

                                    // Special check for Miscellaneous/Mashiyana
                                    if (orderedField.ur === 'مشیانه') {
                                      return fieldNameEn === 'miscellaneous' ||
                                        fieldNameEn === 'mashiyana' ||
                                        fieldNameUr === 'مشیانہ' ||
                                        fieldNameUr === 'مشیانه' ||
                                        fieldNameUr.includes('مشیان');
                                    }

                                    // Check if field matches any of the English or Urdu variations
                                    const matchesEn = orderedField.en.some(enName => {
                                      const enLower = enName.toLowerCase().trim();
                                      return fieldNameEn === enLower ||
                                        fieldNameEn.includes(enLower) ||
                                        enLower.includes(fieldNameEn);
                                    });

                                    const matchesUr = fieldNameUr === orderedField.ur ||
                                      fieldNameUr.includes(orderedField.ur) ||
                                      orderedField.ur.includes(fieldNameUr);

                                    return matchesEn || matchesUr;
                                  });

                                  if (matchedField) {
                                    displayName = isUrdu ? (matchedField.fieldNameUrdu || matchedField.fieldName) : (matchedField.fieldName || matchedField.fieldNameUrdu);

                                    // Special handling for Miscellaneous/Mashiyana
                                    if (orderedField.ur === 'مشیانه' || matchedField.fieldName === 'Miscellaneous' || matchedField.fieldName === 'Mashiyana') {
                                      const mashianaKeys = ['Miscellaneous', 'Mashiyana', 'مشیانہ', 'مشیانه', 'Mashiana', 'Mishiana', matchedField.fieldName, matchedField.fieldNameUrdu];
                                      for (const key of mashianaKeys) {
                                        if (key && billExpenses[key] !== undefined && billExpenses[key] !== null) {
                                          const val = parseFloat(billExpenses[key] || 0);
                                          if (val >= 0) { // Include 0 values
                                            fieldValue = val;
                                            console.log(`Found Mashiana from expenseField match, key "${key}": ${fieldValue}`);
                                            break;
                                          }
                                        }
                                      }
                                    } else {
                                      // Try multiple keys to get the value - check exact match first, then variations
                                      const possibleKeys = [
                                        matchedField.fieldName,
                                        matchedField.fieldNameUrdu,
                                        matchedField.fieldName?.toLowerCase(),
                                        matchedField.fieldNameUrdu?.toLowerCase(),
                                        matchedField.fieldName?.toUpperCase(),
                                        matchedField.fieldNameUrdu?.toUpperCase()
                                      ];

                                      for (const key of possibleKeys) {
                                        if (key && billExpenses[key] !== undefined) {
                                          fieldValue = parseFloat(billExpenses[key] || 0);
                                          if (fieldValue > 0) break;
                                        }
                                      }
                                    }
                                  }
                                }

                                // If not found in expenseFields, try billExpenses directly with all variations
                                if (fieldValue === 0 && Object.keys(billExpenses).length > 0) {
                                  // First try exact matches
                                  for (const [key, value] of Object.entries(billExpenses)) {
                                    const keyLower = key.toLowerCase().trim();
                                    const keyTrimmed = key.trim();
                                    // Remove spaces and special characters for comparison
                                    const keyNormalized = keyLower.replace(/\s+/g, '').replace(/[^\w\u0600-\u06FF]/g, '');

                                    // Check all English variations for exact or partial match
                                    const matchesEn = orderedField.en.some(enName => {
                                      const enLower = enName.toLowerCase().trim();
                                      const enNormalized = enLower.replace(/\s+/g, '').replace(/[^\w\u0600-\u06FF]/g, '');
                                      return keyLower === enLower ||
                                        keyLower.includes(enLower) ||
                                        enLower.includes(keyLower) ||
                                        keyTrimmed === enName ||
                                        keyTrimmed.includes(enName) ||
                                        enName.includes(keyTrimmed) ||
                                        keyNormalized === enNormalized ||
                                        keyNormalized.includes(enNormalized) ||
                                        enNormalized.includes(keyNormalized);
                                    });

                                    // Check Urdu variation (including different Urdu characters)
                                    const urVariations = [orderedField.ur, 'مشیانہ', 'مشیانه']; // Handle different Urdu characters
                                    const matchesUr = urVariations.some(urVar => {
                                      return keyTrimmed === urVar ||
                                        keyTrimmed.includes(urVar) ||
                                        urVar.includes(keyTrimmed) ||
                                        keyNormalized.includes(urVar.replace(/\s+/g, '')) ||
                                        urVar.replace(/\s+/g, '').includes(keyNormalized);
                                    });

                                    if (matchesEn || matchesUr) {
                                      fieldValue = parseFloat(value || 0);
                                      displayName = isUrdu ? orderedField.ur : key;
                                      console.log(`Matched ${orderedField.ur}: key="${key}", value=${fieldValue}`);
                                      break;
                                    }
                                  }
                                }

                                // Final fallback: try to find by removing all spaces and special chars
                                if (fieldValue === 0 && Object.keys(billExpenses).length > 0) {
                                  const targetKeys = orderedField.en.map(e => e.toLowerCase().replace(/\s+/g, '').replace(/[^\w\u0600-\u06FF]/g, ''));
                                  const targetUr = orderedField.ur.replace(/\s+/g, '').replace(/[^\w\u0600-\u06FF]/g, '');

                                  for (const [key, value] of Object.entries(billExpenses)) {
                                    const keyNormalized = key.toLowerCase().replace(/\s+/g, '').replace(/[^\w\u0600-\u06FF]/g, '');

                                    if (targetKeys.some(tk => keyNormalized === tk || keyNormalized.includes(tk) || tk.includes(keyNormalized)) ||
                                      keyNormalized === targetUr || keyNormalized.includes(targetUr) || targetUr.includes(keyNormalized)) {
                                      fieldValue = parseFloat(value || 0);
                                      displayName = isUrdu ? orderedField.ur : key;
                                      console.log(`Fallback matched ${orderedField.ur}: key="${key}", normalized="${keyNormalized}", value=${fieldValue}`);
                                      break;
                                    }
                                  }
                                }

                                displayFields.push({
                                  name: displayName,
                                  value: fieldValue
                                });
                              });

                              return (
                                <div dir={isUrdu ? 'rtl' : 'ltr'}>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {displayFields.map((field, idx) => {
                                      const fieldKey = field.name.toLowerCase().replace(/\s+/g, '_');
                                      const inputValue = expenseFieldInputs[fieldKey] || '';

                                      // Map field index to ref keys based on orderedFields order
                                      const refKeyMap = {
                                        0: 'expense_cash',      // نقدی
                                        1: 'expense_rent',      // کرایه
                                        2: 'expense_commission', // کمیشن
                                        3: 'expense_labor',     // مزدوری
                                        4: 'expense_miscellaneous', // مشیانه
                                        5: 'expense_market_fees' // مارکیٹ فیس
                                      };
                                      const refKey = refKeyMap[idx] || `expense_${fieldKey}`;

                                      // Different colors for each field
                                      const colorClasses = [
                                        'from-blue-50 to-cyan-50 border-blue-300',
                                        'from-purple-50 to-pink-50 border-purple-300',
                                        'from-pink-50 to-rose-50 border-pink-300',
                                        'from-orange-50 to-amber-50 border-orange-300',
                                        'from-green-50 to-emerald-50 border-green-300',
                                        'from-indigo-50 to-violet-50 border-indigo-300'
                                      ];
                                      const colorClass = colorClasses[idx % colorClasses.length];

                                      return (
                                        <div
                                          key={idx}
                                          className={`bg-gradient-to-br ${colorClass} rounded-lg border shadow-sm p-2 hover:shadow-md transition-all duration-200`}
                                        >
                                          <div className={`flex items-center gap-2 ${isUrdu ? 'flex-row-reverse' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                                            {/* Input Field */}
                                            <input
                                              ref={(el) => { expenseFieldInputRefs.current[refKey] = el; }}
                                              type="text"
                                              value={inputValue}
                                              onChange={(e) => {
                                                setExpenseFieldInputs(prev => ({
                                                  ...prev,
                                                  [fieldKey]: e.target.value
                                                }));
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  // Move to next input field
                                                  moveToNextInput(refKey);
                                                }
                                              }}
                                              className={`w-20 px-2 py-1.5 border border-purple-300 rounded-md text-sm font-medium text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-500 transition-all ${isUrdu ? 'text-right' : 'text-left'}`}
                                              placeholder="0"
                                              style={{ textAlign: isUrdu ? 'right' : 'left' }}
                                            />

                                            {/* Field Name Label */}
                                            <div className={`flex-1 flex items-center ${isUrdu ? 'justify-start' : 'justify-end'}`}>
                                              <span className={`text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                                {field.name}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                    </div>
                  );
                })()}

                {/* Send Button - After Last Bank/Payment Method */}
                {(hasBanks || hasPaymentMethods) && (
                  <div className="mt-6 flex items-center justify-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 animate-fadeInUp" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                    <button
                      ref={settleButtonRef}
                      onClick={async () => {
                        if (!selectedSupplierId) {
                          toast.error(companyLanguage === 'ur'
                            ? 'براہ کرم سپلائر منتخب کریں'
                            : 'Please select supplier');
                          return;
                        }

                        // Show confirmation popup
                        const shouldSettle = window.confirm(
                          companyLanguage === 'ur'
                            ? 'کیا آپ بل settled کرنا چاہتے ہیں؟'
                            : 'Do you want to settle the bill?'
                        );

                        // Send all bank payments first
                        const bankPayments = [];
                        for (const [bankFieldKey, fieldData] of Object.entries(bankPaymentFields)) {
                          if (fieldData.amount && parseFloat(fieldData.amount) > 0) {
                            const bank = banksToShow.find(b => {
                              const bankId = b._id?.toString() || b._id;
                              return bankId === bankFieldKey || bankId?.toString() === bankFieldKey;
                            });
                            if (bank) {
                              bankPayments.push({ bankFieldKey, bank, fieldData });
                            }
                          }
                        }

                        // Send all payment method payments
                        const paymentMethodPayments = [];
                        for (const [pmFieldKey, fieldData] of Object.entries(paymentMethodFields)) {
                          if (fieldData.amount && parseFloat(fieldData.amount) > 0) {
                            const pm = paymentMethodsToShow.find(p => {
                              const pmId = p._id?.toString() || p._id;
                              return pmId === pmFieldKey || pmId?.toString() === pmFieldKey;
                            });
                            if (pm) {
                              paymentMethodPayments.push({ pmFieldKey, pm, fieldData });
                            }
                          }
                        }

                        // Send all bank payments
                        for (const { bankFieldKey, bank, fieldData } of bankPayments) {
                          await handleBankPayment(bankFieldKey, bank, fieldData);
                        }

                        // Send all payment method payments
                        for (const { pmFieldKey, pm, fieldData } of paymentMethodPayments) {
                          await handlePaymentMethodPayment(pmFieldKey, pm, fieldData);
                        }

                        // Save expense field inputs as recovery entries ONLY
                        // NOTE: This only creates recovery entries, it does NOT update the original bill data
                        // The updated values in expenseFieldInputs are used for recovery creation only
                        // Original bill.expenses data remains unchanged
                        const selectedBillRow = selectedBillRowId
                          ? supplierRows.find(row => row.id === selectedBillRowId || row.billId === selectedBillRowId)
                          : supplierRows.find(row => {
                            const rowSupplierId = row.supplierName?.toString() || row.supplierName;
                            return rowSupplierId === selectedSupplierId || rowSupplierId?.toString() === selectedSupplierId;
                          });

                        if (selectedBillRow) {
                          const supplierParty = ledgerParties.find(p => {
                            const partyId = p._id?.toString() || p._id;
                            return partyId === selectedSupplierId || partyId?.toString() === selectedSupplierId;
                          });

                          if (supplierParty) {
                            const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);
                            const supplierNameUrdu = supplierParty.nameUrdu || supplierParty.name || '';

                            // Get original bill data to access expense values
                            const billId = selectedBillRow.billId || selectedBillRow.id;
                            const fullBill = bills.find(b => b._id?.toString() === billId?.toString());
                            const billExpenses = fullBill?.expenses || {};

                            // Field name mapping with bill expense keys
                            const fieldMapping = {
                              'نقدی': {
                                en: 'Cash',
                                ur: 'نقدی',
                                billKeys: ['Cash', 'نقدی']
                              },
                              'کرایه': {
                                en: 'Rent',
                                ur: 'کرایه',
                                billKeys: ['Rent', 'کرایه', 'Kiraya']
                              },
                              'کمیشن': {
                                en: 'Commission',
                                ur: 'کمیشن',
                                billKeys: ['Commission', 'کمیشن']
                              },
                              'مزدوری': {
                                en: 'Labor',
                                ur: 'مزدوری',
                                billKeys: ['Labor', 'Wages', 'مزدوری', 'Mazdoori']
                              },
                              'مشیانه': {
                                en: 'Mashiana',
                                ur: 'مشیانه',
                                billKeys: ['Miscellaneous', 'Mashiyana', 'مشیانہ', 'مشیانه', 'Mashiana']
                              },
                              'مارکیٹ_فیس': {
                                en: 'Market Fees',
                                ur: 'مارکیٹ فیس',
                                billKeys: ['Market Fees', 'Market Fee', 'مارکیٹ_فیس', 'مارکیٹ فیس', 'MarketFees']
                              }
                            };

                            // Process all expense fields
                            // If user changed value in expenseFieldInputs, use that
                            // If user didn't change, use original bill expense value
                            for (const [fieldKey, fieldInfo] of Object.entries(fieldMapping)) {
                              let amount = 0;

                              // First check if user entered/changed value in expenseFieldInputs
                              const normalizedKey = fieldKey.toLowerCase().replace(/\s+/g, '_');
                              const inputValue = expenseFieldInputs[normalizedKey];

                              if (inputValue && inputValue.trim() !== '') {
                                // User entered/changed value - use that
                                amount = parseFloat(inputValue) || 0;
                              } else {
                                // User didn't change - use original bill expense value
                                for (const key of fieldInfo.billKeys) {
                                  if (billExpenses[key] !== undefined && billExpenses[key] !== null) {
                                    amount = parseFloat(billExpenses[key] || 0);
                                    if (amount > 0) break;
                                  }
                                }
                              }

                              // Create recovery entry if amount > 0
                              if (amount > 0) {
                                try {
                                  const token = localStorage.getItem("companyToken");

                                  // Create recovery entry with value (either from input or original bill)
                                  // This does NOT update the original bill.expenses
                                  await api.post("/recovery/company/create", {
                                    customerName: supplierName,
                                    customerNameUrdu: supplierNameUrdu,
                                    recoveryAmount: amount,
                                    description: `EXPENSE_FIELD:${fieldInfo.en}:${selectedBillRow.billNumber || selectedBillRow.billId}`,
                                    descriptionUrdu: `${fieldInfo.ur} اخراجات فیلڈ - بل ${selectedBillRow.billNumber || selectedBillRow.billId}`,
                                    date: selectedBillRow.date || new Date().toLocaleDateString('en-CA'),
                                    billId: selectedBillRow.billId || selectedBillRow.id
                                  }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                } catch (error) {
                                  console.error(`Error creating recovery for ${fieldKey}:`, error);
                                }
                              }
                            }
                          }
                        }

                        // If user confirmed to settle, calculate total amount and create sale transaction
                        if (shouldSettle) {
                          const supplierParty = ledgerParties.find(p => {
                            const partyId = p._id?.toString() || p._id;
                            return partyId === selectedSupplierId || partyId?.toString() === selectedSupplierId;
                          });

                          if (supplierParty) {
                            const supplierName = isUrdu ? (supplierParty.nameUrdu || supplierParty.name) : (supplierParty.name || supplierParty.nameUrdu);

                            // Calculate total amount from all rows for this supplier
                            const allSupplierRows = supplierRows.filter(row => {
                              const rowSupplierId = row.supplierName?.toString() || row.supplierName;
                              return rowSupplierId === selectedSupplierId || rowSupplierId?.toString() === selectedSupplierId;
                            });

                            let totalAmount = 0;
                            const billNumbers = [];
                            allSupplierRows.forEach(row => {
                              const amount = parseFloat(row.amount || 0);
                              if (amount > 0) {
                                totalAmount += amount;
                                const billNumber = row.billNumber || row.billNo || row.serialNumber || '';
                                if (billNumber) {
                                  billNumbers.push(billNumber);
                                }
                              }
                            });

                            // Create sale (debit) transaction if total amount > 0
                            if (totalAmount > 0) {
                              try {
                                const token = localStorage.getItem("companyToken");
                                const billNumbersStr = billNumbers.length > 0 ? billNumbers.join(', ') : '';
                                const description = companyLanguage === 'ur'
                                  ? `روزنامچہ - بل settled - ${billNumbersStr ? 'بل: ' + billNumbersStr : ''} - کل رقم: ${totalAmount.toFixed(2)}`
                                  : `Roznamacha - Bill settled - ${billNumbersStr ? 'Bills: ' + billNumbersStr : ''} - Total Amount: ${totalAmount.toFixed(2)}`;

                                const ledgerPayload = {
                                  date: selectedBillRow?.date || new Date().toLocaleDateString('en-CA'),
                                  description: description,
                                  descriptionUrdu: `روزنامچہ - بل settled - ${billNumbersStr ? 'بل: ' + billNumbersStr : ''} - کل رقم: ${totalAmount.toFixed(2)}`,
                                  type: 'debit', // Sale transaction
                                  amount: totalAmount
                                };

                                await api.post(`/ledger/parties/${selectedSupplierId}/transactions`, ledgerPayload, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });

                                console.log(`✅ Created sale transaction for settled bills: ${totalAmount} for supplier ${supplierName}`);

                                // Refresh ledger parties to get updated balances
                                await fetchLedgerParties();
                              } catch (error) {
                                console.error('Error creating sale transaction for settled bills:', error);
                                toast.error(companyLanguage === 'ur'
                                  ? 'سیل لین دین بنانے میں ناکام'
                                  : 'Failed to create sale transaction');
                              }
                            }

                            handleSettleSupplier(selectedSupplierId, supplierName);

                            toast.success(companyLanguage === 'ur'
                              ? `سپلائر settled ہو گیا - ${supplierName}${totalAmount > 0 ? ` - کل رقم: ${totalAmount.toFixed(2)}` : ''}`
                              : `Supplier settled - ${supplierName}${totalAmount > 0 ? ` - Total Amount: ${totalAmount.toFixed(2)}` : ''}`);
                          }
                        } else {
                          toast.success(companyLanguage === 'ur'
                            ? 'ادائیگیاں بھیج دی گئیں'
                            : 'Payments sent successfully');
                        }

                        // Clear all payment fields
                        setBankPaymentFields({});
                        setPaymentMethodFields({});
                        setExpenseFieldInputs({});
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.target.click();
                        }
                      }}
                      disabled={!selectedSupplierId}
                      className={`px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg text-sm font-bold shadow-md transition-all duration-200 hover:shadow-lg transform hover:scale-105 ${!selectedSupplierId ? 'opacity-50 cursor-not-allowed' : ''} ${isUrdu ? 'font-urdu' : ''}`}
                      style={{ boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.3)' }}
                      tabIndex={0}
                    >
                      {isUrdu ? 'بھیجیں' : 'Send'}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Suppliers Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            {/* Suppliers Heading */}
            <h2 className={`text-4xl font-bold text-gray-900 mb-6 text-center ${isUrdu ? 'font-urdu' : ''}`}>
              {isUrdu ? 'سپلائر' : 'Suppliers'}
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse" style={{ border: '1px solid #000' }}>
                <thead>
                  <tr style={{ backgroundColor: '#D3D3D3' }}>
                    {supplierColumns.filter(col => col.key !== 'amount').map((col, idx) => {
                      return (
                        <th
                          key={idx}
                          className={isUrdu ? 'font-urdu' : ''}
                          style={{
                            border: '1px solid #000',
                            padding: '8px',
                            textAlign: col.type === 'number' ? 'right' : 'center',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                          }}
                        >
                          <span>{isUrdu ? col.labelUr : col.labelEn}</span>
                        </th>
                      );
                    })}
                    <th
                      className={isUrdu ? 'font-urdu' : ''}
                      style={{
                        border: '1px solid #000',
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                      }}
                    >
                      {isUrdu ? 'اعمال' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplierRows.map((row, index) => (
                    <tr key={row.id} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9' }}>
                      {supplierColumns.filter(col => col.key !== 'amount').map((col, colIdx) => {
                        const colKey = col.key;
                        const colValue = row[colKey] || '';
                        const isReadOnly = row.billId && (colKey === 'date' || colKey === 'billNumber' || colKey === 'supplierName' || colKey === 'sale' || colKey === 'cash');

                        return (
                          <td
                            key={colIdx}
                            className={isUrdu ? 'font-urdu' : ''}
                            style={{
                              border: '1px solid #000',
                              padding: '4px',
                              textAlign: col.type === 'number' ? 'right' : 'center',
                              fontSize: '12px',
                              fontWeight: colKey === 'balance' ? 'bold' : 'normal',
                              fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                            }}
                          >
                            {colKey === 'date' ? (
                              isReadOnly ? (
                                <span className={isUrdu ? 'font-urdu' : ''}>{colValue}</span>
                              ) : (
                                <input
                                  type="date"
                                  value={colValue}
                                  onChange={(e) => handleSupplierRowChange(row.id, colKey, e.target.value)}
                                  className={isUrdu ? 'font-urdu' : ''}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: '12px',
                                    padding: '2px',
                                    fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                                  }}
                                />
                              )
                            ) : colKey === 'supplierName' ? (
                              isReadOnly ? (
                                <span
                                  className={isUrdu ? 'font-urdu' : ''}
                                  onClick={() => {
                                    const supplierNameValue = row.supplierName || '';
                                    const isObjectId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);

                                    if (isObjectId && supplierNameValue) {
                                      // Auto-select this supplier and specific bill in shared supplier field
                                      const supplierId = supplierNameValue;
                                      setSelectedSupplierId(supplierId);
                                      // Track the specific bill row that was clicked
                                      setSelectedBillRowId(row.id || row.billId || null);

                                      toast.success(companyLanguage === 'ur'
                                        ? 'سپلائر منتخب ہو گیا'
                                        : 'Supplier selected');
                                    }
                                  }}
                                  style={{
                                    cursor: /^[0-9a-fA-F]{24}$/.test(row.supplierName || '') ? 'pointer' : 'default',
                                    color: /^[0-9a-fA-F]{24}$/.test(row.supplierName || '') ? '#2563eb' : 'inherit',
                                    textDecoration: /^[0-9a-fA-F]{24}$/.test(row.supplierName || '') ? 'underline' : 'none'
                                  }}
                                  title={/^[0-9a-fA-F]{24}$/.test(row.supplierName || '') ? (isUrdu ? 'اوپر والے fields میں منتخب کریں' : 'Click to select in top fields') : ''}
                                >
                                  {(() => {
                                    // Check if supplierName is a party ID, if so get the actual name
                                    let name = '';
                                    const supplierNameValue = row.supplierName || '';

                                    // Check if it's a MongoDB ObjectId (24 hex characters)
                                    const isObjectId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);

                                    if (isObjectId && ledgerParties.length > 0) {
                                      // Look up the party name from the ID
                                      const party = ledgerParties.find(p => {
                                        const partyId = p._id?.toString() || p._id;
                                        return partyId === supplierNameValue || partyId?.toString() === supplierNameValue;
                                      });

                                      if (party) {
                                        // Use the actual party name (Urdu if available, otherwise English)
                                        name = isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu);
                                      } else {
                                        // Party not found, hide ID and use fallback
                                        name = isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '');
                                      }
                                    } else {
                                      // Not an ID, check if it looks like an ID anyway and hide it
                                      const looksLikeId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);
                                      if (looksLikeId) {
                                        // Hide ID, use fallback
                                        name = isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '');
                                      } else {
                                        // Not an ID, use the value as is or fallback to customerName
                                        name = supplierNameValue || (isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '-'));
                                      }
                                    }

                                    // Get phone from row (from bill) or from ledger party
                                    const phone = row.customerPhone || (() => {
                                      const party = ledgerParties.find(p => {
                                        const partyId = p._id?.toString() || p._id;
                                        return partyId === supplierNameValue || partyId?.toString() === supplierNameValue;
                                      });
                                      return party?.phone || '';
                                    })();
                                    // Get vehicle number (گاڑی نمبر) from row
                                    const vehicle = row.vehicleNumber || '';

                                    // Build display string: name - phone - vehicle number
                                    let displayText = name;
                                    if (phone) {
                                      displayText += ` - ${phone}`;
                                    }
                                    if (vehicle) {
                                      displayText += ` - ${isUrdu ? 'گاڑی نمبر' : 'Vehicle Number'}: ${vehicle}`;
                                    }
                                    return displayText;
                                  })()}
                                  {/* Click to select supplier in top fields */}
                                  {(() => {
                                    const supplierNameValue = row.supplierName || '';
                                    const isObjectId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);

                                    if (isObjectId && supplierNameValue) {
                                      const handleSelect = () => {
                                        const supplierId = supplierNameValue;
                                        setSelectedSupplierId(supplierId);
                                        // Track the specific bill row that was clicked
                                        setSelectedBillRowId(row.id || row.billId || null);

                                        toast.success(companyLanguage === 'ur'
                                          ? 'سپلائر منتخب ہو گیا'
                                          : 'Supplier selected');
                                      };

                                      return (
                                        <span
                                          onClick={handleSelect}
                                          className="text-xs text-blue-600 hover:text-blue-800 underline ml-2"
                                          style={{ fontSize: '10px', cursor: 'pointer' }}
                                          title={isUrdu ? 'اوپر والے fields میں منتخب کریں' : 'Click to select in top fields'}
                                        >
                                          {isUrdu ? ' (منتخب)' : ' (Select)'}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </span>
                              ) : (
                                (() => {
                                  // Check if colValue is an ID and if it matches any option
                                  const isObjectId = /^[0-9a-fA-F]{24}$/.test(colValue);
                                  let selectValue = colValue;

                                  if (isObjectId && ledgerParties.length > 0) {
                                    // Check if this ID exists in the supplier options
                                    const supplierParties = ledgerParties.filter(p => p.type === 'supplier');
                                    const partyExists = supplierParties.some(p => {
                                      const partyId = p._id?.toString() || p._id;
                                      return partyId === colValue || partyId?.toString() === colValue;
                                    });

                                    if (!partyExists) {
                                      // ID doesn't match any option, reset to empty to hide ID
                                      selectValue = '';
                                    }
                                  } else if (isObjectId) {
                                    // Looks like ID but no parties loaded yet, hide it
                                    selectValue = '';
                                  }

                                  return (
                                    <select
                                      value={selectValue}
                                      onChange={(e) => handleSupplierRowChange(row.id, colKey, e.target.value)}
                                      className={isUrdu ? 'font-urdu' : ''}
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        fontSize: '12px',
                                        padding: '2px',
                                        fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                                      }}
                                    >
                                      <option value="">{isUrdu ? 'سپلائر منتخب کریں' : 'Select Supplier'}</option>
                                      {ledgerParties.filter(p => p.type === 'supplier').map(party => (
                                        <option key={party._id} value={party._id}>
                                          {isUrdu ? (party.nameUrdu || party.name) : party.name}
                                          {party.phone ? ` - ${party.phone}` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  );
                                })()
                              )
                            ) : col.type === 'number' ? (
                              // Check if this is a bank or payment method column
                              (colKey && (colKey.startsWith('bank_') || colKey.startsWith('paymentMethod_'))) ? (
                                // Bank/Payment Method field - DISABLED (only show amount from transactions)
                                <input
                                  type="number"
                                  value={colValue}
                                  disabled
                                  readOnly
                                  className={isUrdu ? 'font-urdu' : ''}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    background: '#f3f4f6',
                                    fontSize: '12px',
                                    padding: '2px',
                                    textAlign: 'right',
                                    fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif',
                                    cursor: 'not-allowed',
                                    color: '#6b7280'
                                  }}
                                  placeholder="0"
                                />
                              ) : isReadOnly && (colKey === 'sale' || colKey === 'cash') ? (
                                <span className={isUrdu ? 'font-urdu' : ''}>{colValue}</span>
                              ) : (
                                <input
                                  type="number"
                                  value={colValue}
                                  onChange={(e) => handleSupplierRowChange(row.id, colKey, e.target.value)}
                                  className={isUrdu ? 'font-urdu' : ''}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: '12px',
                                    padding: '2px',
                                    textAlign: 'right',
                                    fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                                  }}
                                  placeholder="0"
                                />
                              )
                            ) : (
                              <input
                                type={col.type || 'text'}
                                value={colValue}
                                onChange={(e) => handleSupplierRowChange(row.id, colKey, e.target.value)}
                                className={isUrdu ? 'font-urdu' : ''}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent',
                                  fontSize: '12px',
                                  padding: '2px',
                                  fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                                }}
                                placeholder=""
                              />
                            )}
                          </td>
                        );
                      })}
                      <td
                        className={isUrdu ? 'font-urdu' : ''}
                        style={{
                          border: '1px solid #000',
                          padding: '4px',
                          textAlign: 'center',
                          fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                        }}
                      >
                        <div className="flex flex-col gap-1 items-center">
                          {row.billId && (
                            <button
                              onClick={() => handleViewSettlement(row)}
                              className={isUrdu ? 'font-urdu' : ''}
                              style={{
                                color: '#2563EB',
                                fontSize: '11px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif',
                                textDecoration: 'underline'
                              }}
                              title={isUrdu ? 'سیٹلمنٹ دیکھیں' : 'View Settlement'}
                            >
                              {isUrdu ? 'سیٹلمنٹ' : 'Settlement'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteRow(row.id, 'supplier')}
                            className={isUrdu ? 'font-urdu' : ''}
                            style={{
                              color: '#DC2626',
                              fontSize: '12px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                            }}
                          >
                            {isUrdu ? 'ختم کریں' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end items-center">
              <div className="flex gap-4">
                <button
                  onClick={() => setEditingRowId(null)}
                  className={`px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'تبدیل کریں' : 'Edit'}
                </button>
              </div>
            </div>
          </div>

          {/* Settled Suppliers Section - Always visible */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-lg p-6 mb-8 border-2 border-green-200 animate-fadeInUp" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <h2 className={`text-3xl font-bold text-gray-900 mb-6 text-center ${isUrdu ? 'font-urdu' : ''}`}>
              {isUrdu ? `✅ Settled شدہ سپلائر (${settledSuppliers.length})` : `✅ Settled Suppliers (${settledSuppliers.length})`}
            </h2>

            {settledSuppliers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className={isUrdu ? 'font-urdu' : ''}>
                  {isUrdu ? 'ابھی تک کوئی settled supplier نہیں' : 'No settled suppliers yet'}
                </p>
              </div>
            ) : (

              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse" style={{ border: '1px solid #000' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#86efac' }}>
                      {supplierColumns.filter(col => col.key !== 'amount').map((col, idx) => {
                        return (
                          <th
                            key={idx}
                            className={isUrdu ? 'font-urdu' : ''}
                            style={{
                              border: '1px solid #000',
                              padding: '8px',
                              textAlign: col.type === 'number' ? 'right' : 'center',
                              fontWeight: 'bold',
                              fontSize: '12px',
                              fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                            }}
                          >
                            <span>{isUrdu ? col.labelUr : col.labelEn}</span>
                          </th>
                        );
                      })}
                      <th
                        className={isUrdu ? 'font-urdu' : ''}
                        style={{
                          border: '1px solid #000',
                          padding: '8px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                        }}
                      >
                        {isUrdu ? 'اعمال' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {settledSuppliers.map((row, index) => (
                      <tr key={row.id} style={{ backgroundColor: index % 2 === 0 ? '#f0fdf4' : '#dcfce7' }}>
                        {supplierColumns.filter(col => col.key !== 'amount').map((col, colIdx) => {
                          const colKey = col.key;
                          const colValue = row[colKey] || '';

                          return (
                            <td
                              key={colIdx}
                              className={isUrdu ? 'font-urdu' : ''}
                              style={{
                                border: '1px solid #000',
                                padding: '4px',
                                textAlign: col.type === 'number' ? 'right' : 'center',
                                fontSize: '12px',
                                fontWeight: colKey === 'balance' ? 'bold' : 'normal',
                                fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                              }}
                            >
                              {colKey === 'supplierName' ? (
                                (() => {
                                  const supplierNameValue = row.supplierName || '';
                                  const isObjectId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);

                                  let name = '';

                                  if (isObjectId && ledgerParties.length > 0) {
                                    // Look up the party name from the ID
                                    const party = ledgerParties.find(p => {
                                      const partyId = p._id?.toString() || p._id;
                                      return partyId === supplierNameValue || partyId?.toString() === supplierNameValue;
                                    });

                                    if (party) {
                                      // Use the actual party name (Urdu if available, otherwise English)
                                      name = isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu);
                                    } else {
                                      // Party not found, hide ID and use fallback
                                      name = isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '');
                                    }
                                  } else {
                                    // Not an ID, check if it looks like an ID anyway and hide it
                                    const looksLikeId = /^[0-9a-fA-F]{24}$/.test(supplierNameValue);
                                    if (looksLikeId) {
                                      // Hide ID, use fallback
                                      name = isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '');
                                    } else {
                                      // Not an ID, use the value as is or fallback to customerName
                                      name = supplierNameValue || (isUrdu ? (row.customerNameUrdu || row.customerName) : (row.customerName || row.customerNameUrdu || '-'));
                                    }
                                  }

                                  // Get phone from row (from bill) or from ledger party
                                  const phone = row.customerPhone || (() => {
                                    const party = ledgerParties.find(p => {
                                      const partyId = p._id?.toString() || p._id;
                                      return partyId === supplierNameValue || partyId?.toString() === supplierNameValue;
                                    });
                                    return party?.phone || '';
                                  })();
                                  // Get vehicle number (گاڑی نمبر) from row
                                  const vehicle = row.vehicleNumber || '';

                                  // Build display string: name - phone - vehicle number
                                  let displayText = name;
                                  if (phone) {
                                    displayText += ` - ${phone}`;
                                  }
                                  if (vehicle) {
                                    displayText += ` - ${isUrdu ? 'گاڑی نمبر' : 'Vehicle Number'}: ${vehicle}`;
                                  }
                                  return displayText || '-';
                                })()
                              ) : colKey === 'amount' ? (
                                // Make amount field editable in settled bills
                                <input
                                  type="number"
                                  value={colValue || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setSettledSuppliers(prevSettled => {
                                      const updatedSettled = prevSettled.map(s => {
                                        if (s.id === row.id || (s.billId && row.billId && s.billId.toString() === row.billId.toString())) {
                                          const updatedRow = { ...s, [colKey]: newValue };

                                          // Recalculate balance when amount changes
                                          const sale = parseFloat(updatedRow.sale || 0);
                                          const cash = parseFloat(updatedRow.cash || 0);
                                          const amount = parseFloat(updatedRow.amount || 0);

                                          // Sum all bank columns
                                          let totalBanks = 0;
                                          supplierColumns.forEach(col => {
                                            if (col.key && col.key.startsWith('bank_')) {
                                              totalBanks += parseFloat(updatedRow[col.key] || 0);
                                            }
                                          });

                                          // Sum all payment method columns
                                          let totalPaymentMethods = 0;
                                          supplierColumns.forEach(col => {
                                            if (col.key && col.key.startsWith('paymentMethod_')) {
                                              totalPaymentMethods += parseFloat(updatedRow[col.key] || 0);
                                            }
                                          });

                                          // Calculate balance: sale minus all payments
                                          const calculatedBalance = sale - cash - totalBanks - totalPaymentMethods - amount;
                                          updatedRow.balance = calculatedBalance.toFixed(2);

                                          return updatedRow;
                                        }
                                        return s;
                                      });

                                      // Save to localStorage
                                      if (company?._id) {
                                        try {
                                          const settledKey = `rozSettledSuppliers_${company._id}`;
                                          localStorage.setItem(settledKey, JSON.stringify(updatedSettled));
                                        } catch (error) {
                                          console.error("Error saving settled suppliers:", error);
                                        }
                                      }

                                      return updatedSettled;
                                    });
                                  }}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    padding: '2px'
                                  }}
                                  className={isUrdu ? 'font-urdu' : ''}
                                />
                              ) : (colKey && (colKey.startsWith('bank_') || colKey.startsWith('paymentMethod_'))) ? (
                                // Bank/Payment Method columns - display as read-only number
                                <span style={{
                                  display: 'block',
                                  textAlign: 'right',
                                  fontWeight: parseFloat(colValue || 0) > 0 ? 'bold' : 'normal',
                                  color: parseFloat(colValue || 0) > 0 ? '#059669' : '#6b7280'
                                }}>
                                  {parseFloat(colValue || 0) > 0 ? parseFloat(colValue).toFixed(2) : '-'}
                                </span>
                              ) : col.type === 'number' ? (
                                // Other number columns
                                <span style={{ textAlign: 'right' }}>
                                  {colValue ? parseFloat(colValue).toFixed(2) : '-'}
                                </span>
                              ) : (
                                <span>{colValue || '-'}</span>
                              )}
                            </td>
                          );
                        })}
                        <td
                          className={isUrdu ? 'font-urdu' : ''}
                          style={{
                            border: '1px solid #000',
                            padding: '4px',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontFamily: isUrdu ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'Arial, sans-serif'
                          }}
                        >
                          <button
                            onClick={() => {
                              if (window.confirm(companyLanguage === 'ur'
                                ? 'کیا آپ واقعی اس settled bill کو حذف کرنا چاہتے ہیں؟'
                                : 'Are you sure you want to delete this settled bill?')) {
                                // Delete settled bill
                                setSettledSuppliers(prevSettled => {
                                  const filtered = prevSettled.filter(s => {
                                    const sBillId = s.billId || s.id;
                                    const rowBillId = row.billId || row.id;
                                    return !(sBillId && rowBillId && sBillId.toString() === rowBillId.toString());
                                  });

                                  // Save to localStorage
                                  if (company?._id) {
                                    try {
                                      const settledKey = `rozSettledSuppliers_${company._id}`;
                                      localStorage.setItem(settledKey, JSON.stringify(filtered));
                                      console.log('Deleted settled bill and saved to localStorage');
                                    } catch (error) {
                                      console.error("Error saving after delete:", error);
                                    }
                                  }

                                  toast.success(companyLanguage === 'ur'
                                    ? 'Settled bill حذف ہو گیا'
                                    : 'Settled bill deleted');

                                  return filtered;
                                });
                              }
                            }}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                            style={{
                              cursor: 'pointer',
                              fontSize: '11px',
                              padding: '4px 8px'
                            }}
                          >
                            {isUrdu ? 'ختم کریں' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settlement Modal */}
      {showSettlementModal && selectedBillSettlement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-2xl font-bold ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'بل سیٹلمنٹ' : 'Bill Settlement'}
              </h2>
              <button
                onClick={() => {
                  setShowSettlementModal(false);
                  setSelectedBillSettlement(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Sales Value */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className={`font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'سیل کی قیمت:' : 'Sales Value:'}
                  </span>
                  <span className="text-xl font-bold text-blue-600">
                    {selectedBillSettlement.sale.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Payments Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className={`font-semibold mb-3 ${isUrdu ? 'font-urdu' : ''}`}>
                  {isUrdu ? 'ادائیگیاں:' : 'Payments:'}
                </h3>

                {/* Cash Payment */}
                {selectedBillSettlement.cash > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className={isUrdu ? 'font-urdu' : ''}>
                      {isUrdu ? 'نقدی:' : 'Cash:'}
                    </span>
                    <span className="font-semibold">{selectedBillSettlement.cash.toFixed(2)}</span>
                  </div>
                )}

                {/* نقد رقم */}
                {selectedBillSettlement.amount > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className={isUrdu ? 'font-urdu' : ''}>
                      {isUrdu ? 'نقد رقم:' : 'Cash Amount:'}
                    </span>
                    <span className="font-semibold">{selectedBillSettlement.amount.toFixed(2)}</span>
                  </div>
                )}

                {/* Bank Payments */}
                {selectedBillSettlement.bankPayments.length > 0 && (
                  <div className="mt-3">
                    <h4 className={`font-semibold mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'بینک ادائیگیاں:' : 'Bank Payments:'}
                    </h4>
                    {selectedBillSettlement.bankPayments.map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center mb-1 ml-4">
                        <span className={isUrdu ? 'font-urdu' : ''}>
                          {payment.bankName}:
                        </span>
                        <span className="font-semibold">{payment.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-300 ml-4">
                      <span className={`font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'کل بینک ادائیگیاں:' : 'Total Bank Payments:'}
                      </span>
                      <span className="font-bold text-green-600">
                        {selectedBillSettlement.totalBankPayments.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Payment Method Payments */}
                {selectedBillSettlement.paymentMethodPayments.length > 0 && (
                  <div className="mt-3">
                    <h4 className={`font-semibold mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'ادائیگی کے طریقے:' : 'Payment Methods:'}
                    </h4>
                    {selectedBillSettlement.paymentMethodPayments.map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center mb-1 ml-4">
                        <span className={isUrdu ? 'font-urdu' : ''}>
                          {payment.methodName}:
                        </span>
                        <span className="font-semibold">{payment.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-300 ml-4">
                      <span className={`font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'کل ادائیگی کے طریقے:' : 'Total Payment Methods:'}
                      </span>
                      <span className="font-bold text-green-600">
                        {selectedBillSettlement.totalPaymentMethodPayments.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Total Payments */}
                <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-gray-400">
                  <span className={`text-lg font-bold ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'کل ادائیگیاں:' : 'Total Payments:'}
                  </span>
                  <span className="text-xl font-bold text-green-600">
                    {selectedBillSettlement.totalPayments.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Balance */}
              <div className={`p-4 rounded-lg ${selectedBillSettlement.isFullyPaid ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex justify-between items-center">
                  <span className={`font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'بقایا:' : 'Balance:'}
                  </span>
                  <span className={`text-xl font-bold ${selectedBillSettlement.isFullyPaid ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedBillSettlement.balance.toFixed(2)}
                    {selectedBillSettlement.isFullyPaid && ` (${isUrdu ? 'مکمل ادا' : 'Fully Paid'})`}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {isUrdu ? `ادائیگی: ${selectedBillSettlement.paymentPercentage}%` : `Payment: ${selectedBillSettlement.paymentPercentage}%`}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowSettlementModal(false);
                  setSelectedBillSettlement(null);
                }}
                className={`px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg ${isUrdu ? 'font-urdu' : ''}`}
              >
                {isUrdu ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SettledBill;

